// Phase 6.D-8 — 자동발주 화면 (/inventory/auto).
// 후보 카드는 화면에서 계산하고, 발주서 draft 는 사용자가 직접 버튼으로 생성한다.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  // N2 (2026-05-08) — inventoryControllerLowStock 폐기. listMaterials + client filter.
  inventoryControllerListMaterials,
  locationsControllerList,
  ordersControllerCreate,
  ordersControllerList,
  ordersControllerRemove,
  ordersControllerUpdate,
  suppliersControllerList,
} from '@dvvb/sdk';
import {
  AlertBanner,
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Icon,
  Modal,
  Select,
  TextInput,
  Textarea,
  useToast,
} from '../../components/primitives';
import { unwrap, ApiCallError } from '../../lib/envelope';
import { API_BASE, getStoredToken } from '../../lib/api-client';
import { queryClient } from '../../lib/queryClient';
import { useViewport } from '../../lib/useViewport';
import {
  autoOrderMaterialShortage,
  buildPendingQuantityByInventoryId,
  loadAllPendingOrders,
  pendingOrdersGate,
  roundOrderQuantity,
  type PendingOrderPageRequest,
  type PendingOrdersGateReason,
} from './auto-order-data';
import './supplier-auto-design.css';

// ─── Types ────────────────────────────────────────────────────────────
interface OrderLineRow {
  id: string;
  inventoryId: string;
  inventoryName: string | null;
  locationId: string | null;
  locationName: string | null;
  unit: string | null;
  quantity: string; // Decimal → string
  unitCost: string;
  totalCost: string;
}

interface OrderRow {
  id: string;
  companyId: string;
  supplierId: string;
  supplier: { id: string; name: string; smsRecipient: string | null } | null;
  status: 'draft' | 'sent' | 'received' | 'cancelled';
  totalAmount: string;
  expectedDate: string | null;
  isAutoGenerated: boolean;
  scheduledFor: string | null;
  notes: string | null;
  createdAt: string;
  sentAt: string | null;
  receivedAt: string | null;
  lines: OrderLineRow[];
}

type OrderStatusFilter = 'work' | 'sent' | 'received' | 'cancelled';

interface OrdersEnvelope {
  items: OrderRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface SupplierRow {
  id: string;
  name: string;
  phone: string | null;
  contactName: string | null;
  autoOrderEnabled: boolean;
  defaultLeadTimeDays?: number;
  smsRecipient?: string | null;
  metadata?: Record<string, unknown>;
  status: string;
}

function supplierMetadataString(
  metadata: Record<string, unknown> | undefined,
  key: string,
  fallback = '',
): string {
  const value = metadata?.[key];
  return typeof value === 'string' ? value : fallback;
}

function isOnlineSupplier(metadata: Record<string, unknown> | undefined): boolean {
  return supplierMetadataString(metadata, 'channelType', 'offline') === 'online';
}

function isCoupangOnlineSupplier(
  supplier: SupplierRow | null,
  metadata: Record<string, unknown> | undefined,
  platformUrl: string,
): boolean {
  if (!isOnlineSupplier(metadata)) {
    return false;
  }

  const channel = supplierMetadataString(metadata, 'channel').toLowerCase();
  const searchableText = [
    supplier?.name,
    channel,
    platformUrl,
  ].filter(Boolean).join(' ').toLowerCase();

  return channel === 'coupang' || searchableText.includes('coupang') || searchableText.includes('쿠팡');
}

type CoupangMcpStageStatus = 'done' | 'active' | 'pending' | 'blocked';

interface CoupangMcpStageView {
  label: string;
  status: CoupangMcpStageStatus;
  detail: string;
}

type OnlineOrderRunnerJobStatus =
  | 'queued'
  | 'running'
  | 'login_required'
  | 'captcha_required'
  | 'review_required'
  | 'cart_ready'
  | 'failed'
  | 'aborted';

interface OnlineOrderRunnerJobLine {
  lineId: string;
  inventoryId: string;
  materialName: string;
  status: string;
  requestedQuantity: number;
  orderUnit: string;
  productTitle?: string;
  optionText?: string;
  cartQuantity?: number;
  evidence?: Record<string, unknown>;
  blockers: string[];
}

interface OnlineOrderRunnerJobRecord {
  jobId: string;
  status: OnlineOrderRunnerJobStatus;
  executionMode: 'fixture' | 'dry_run' | 'live_cart';
  createdAt: string;
  updatedAt: string;
  sessionId: string;
  browserUrl?: string;
  lines: OnlineOrderRunnerJobLine[];
  evidence?: {
    jobDir?: string;
    resultJson?: string;
    progressJsonl?: string;
    screenshotsDir?: string;
    htmlDir?: string;
  };
  blockers: string[];
  result?: Record<string, unknown>;
}

interface OnlineOrderRunnerJobCreateResponse {
  item: OnlineOrderRunnerJobRecord;
  request: unknown;
  boundaries: string[];
}

interface OnlineOrderRunnerJobResponse {
  item: OnlineOrderRunnerJobRecord;
}

interface OnlineOrderRunnerJobBrowserResponse {
  item: OnlineOrderRunnerJobRecord;
  browser?: {
    status?: string;
    url?: string;
    openedUrl?: string;
    blockers?: string[];
  };
}

interface OnlineOrderRunnerWritebackResponse {
  item: {
    id: string;
    status: string;
    sentAt?: string | null;
    notes?: string | null;
  };
  runnerJob: {
    jobId: string;
    status: string;
    evidenceRoot?: string;
  };
}

interface SuppliersEnvelope {
  items: SupplierRow[];
  count: number;
}

// N2 (2026-05-08) — inventory.materials list (= /v1/inventory/materials)
interface MaterialRow {
  id: string;
  locationId: string;
  locationName: string | null;
  name: string;
  unit: string;
  cost: number;
  minStock: number;
  targetStock: number;
  stock: number;
  status: string;
  providerKind: 'boxhero' | 'dvvb' | null;
  providerItemId: string | null;
  providerUnit: string | null;
  conversionRatio: number | null;
  comparableQuantity: number | null;
  comparableUnit: string;
  orderEquivalentQuantity: number | null;
  expiryDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MaterialsEnvelope {
  items: MaterialRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface LocationRow {
  id: string;
  name: string;
  settings?: Record<string, unknown>;
}

interface LocationsEnvelope {
  items: LocationRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface OrderSmsSenderSettings {
  displayName: string;
  phone: string;
  address: string;
  businessName: string;
  businessNumber: string;
  defaultMemo: string;
}

const ORDER_SMS_SENDER_SETTINGS_KEY = 'autoOrderSmsSender';

interface SupplierSupplyItem {
  sourceId: string | null;
  sourceName: string;
  supplierName: string;
  memo: string;
  sourceLocationId: string | null;
  sourceLocationName: string | null;
  autoEnabled: '사용' | '미사용';
}

interface CandidateLocation {
  id: string;
  name: string;
}

interface AutoOrderCandidateLine {
  inventoryId: string;
  inventoryName: string;
  materialName: string;
  locationId: string;
  locationName: string;
  unit: string;
  shortageQuantity: number;
  pendingQuantity: number;
  quantity: number;
  unitCost: number;
  totalCost: number;
  providerKind: MaterialRow['providerKind'];
  expiryDate: string | null;
  expiryDays: number | null;
  expiryStatus: 'none' | 'soon' | 'expired';
}

interface AutoOrderLocationGroup {
  locationId: string;
  locationName: string;
  lines: AutoOrderCandidateLine[];
  totalQuantity: number;
  totalAmount: number;
}

interface AutoOrderSupplierGroup {
  supplier: SupplierRow;
  locationGroups: AutoOrderLocationGroup[];
  totalLines: number;
  totalAmount: number;
}

type CandidateReviewStatus = 'pending' | 'confirmed' | 'excluded';
type SimulationStatus = 'idle' | 'running' | 'waiting' | 'approved' | 'modified' | 'skipped';

interface SimulationMessage {
  id: string;
  kind: 'system' | 'user';
  text: string;
}

interface SimulationHistoryEntry {
  id: string;
  time: string;
  text: string;
}

const CANDIDATE_REVIEW_LABEL: Record<CandidateReviewStatus, string> = {
  pending: '대기',
  confirmed: '확인 완료',
  excluded: '제외',
};

const SIMULATION_STATUS_LABEL: Record<SimulationStatus, string> = {
  idle: '대기 중',
  running: '시뮬레이션 실행 중',
  waiting: '승인 대기',
  approved: '승인 완료',
  modified: '수량 수정 요청',
  skipped: '오늘 제외',
};

const SIMULATION_TIMELINE = [
  {
    title: '대기 리스트 확인',
    detail: '거래처 공급물품 매핑과 후보 품목을 확인합니다.',
  },
  {
    title: '거래처/사업장 분리',
    detail: '후보를 거래처와 사업장 단위로 묶습니다.',
  },
  {
    title: '승인 메시지 작성',
    detail: '확인 완료 후보를 기준으로 사장님 폰 미리보기를 만듭니다.',
  },
  {
    title: '관리자 승인 대기',
    detail: '전체 승인, 수량 수정, 오늘 제외 중 선택을 기다립니다.',
  },
  {
    title: '결과 반영',
    detail: '선택 결과를 이력에 남깁니다. 실제 외부 발송은 하지 않습니다.',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────
function formatWon(s: string | number): string {
  const n = typeof s === 'string' ? Number(s) : s;
  return '₩' + (Number.isFinite(n) ? Math.round(n) : 0).toLocaleString('ko-KR');
}

function formatNumber(s: string | number): string {
  const n = typeof s === 'string' ? Number(s) : s;
  return Number.isFinite(n) ? n.toLocaleString('ko-KR', { maximumFractionDigits: 4 }) : '0';
}

async function callDvvbApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json; charset=utf-8');
  }
  const token = getStoredToken();
  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });
  const data = await response.json().catch(() => null);
  return unwrap<T>({ data, response });
}

function isOnlineOrderRunnerTerminalStatus(status: OnlineOrderRunnerJobStatus): boolean {
  return [
    'login_required',
    'captcha_required',
    'review_required',
    'cart_ready',
    'failed',
    'aborted',
  ].includes(status);
}

function onlineOrderRunnerStatusLabel(status: OnlineOrderRunnerJobStatus): string {
  const labels: Record<OnlineOrderRunnerJobStatus, string> = {
    queued: '대기 중',
    running: '실행 중',
    login_required: '로그인 필요',
    captcha_required: '보안 확인 필요',
    review_required: '검수 필요',
    cart_ready: '장바구니 준비',
    failed: '실패',
    aborted: '중단됨',
  };
  return labels[status];
}

function onlineOrderRunnerBadgeTone(status: OnlineOrderRunnerJobStatus): 'success' | 'warning' | 'danger' | 'info' {
  if (status === 'cart_ready') return 'success';
  if (status === 'failed' || status === 'aborted') return 'danger';
  if (status === 'queued' || status === 'running') return 'info';
  return 'warning';
}

function daysUntilDate(date: string | null | undefined): number | null {
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return null;
  const expiry = new Date(year, month - 1, day);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((expiry.getTime() - today.getTime()) / 86_400_000);
}

function materialExpiryState(m: MaterialRow): {
  expiryDate: string | null;
  expiryDays: number | null;
  expiryStatus: AutoOrderCandidateLine['expiryStatus'];
} {
  const expiryDate = m.expiryDate ?? null;
  const expiryDays = daysUntilDate(expiryDate);
  return {
    expiryDate,
    expiryDays,
    expiryStatus:
      expiryDays == null ? 'none' : expiryDays < 0 ? 'expired' : expiryDays <= 1 ? 'soon' : 'none',
  };
}

function materialShortage(m: MaterialRow): number {
  return autoOrderMaterialShortage(m, materialExpiryState(m).expiryStatus === 'expired');
}

function autoOrderLineStateLabel(line: AutoOrderCandidateLine): string {
  if (line.expiryStatus === 'expired') return '만료 재고 제외';
  if (line.quantity <= 0 && line.pendingQuantity > line.shortageQuantity) return '발주중 초과';
  if (line.quantity <= 0 && line.pendingQuantity > 0) return '발주중';
  if (line.expiryStatus === 'soon') return '유통기한 확인';
  return '';
}

function autoOrderLineNote(line: AutoOrderCandidateLine): string {
  if (line.expiryStatus === 'expired') {
    return '유통기한 만료 재고는 가용 재고에서 제외하고 적정수량 기준으로 다시 계산합니다.';
  }
  if (line.expiryStatus === 'soon') {
    const label = line.expiryDays === 0 ? '오늘까지 사용 가능' : `${line.expiryDays ?? 1}일 남음`;
    return `유통기한 임박(${label}) - 사용/폐기/재고조정 판단이 필요합니다.`;
  }
  if (line.quantity <= 0 && line.pendingQuantity > line.shortageQuantity) {
    return '발주중 수량이 현재 필요보다 많습니다. 필요 없으면 발주서에서 발주중 제외 후 다시 생성하세요.';
  }
  if (line.quantity <= 0 && line.pendingQuantity > 0) {
    return '현재 필요 수량이 기존 발주서로 커버되어 추가 발주할 수량이 없습니다.';
  }
  return '';
}

function candidateReviewKey(
  supplierId: string,
  locationId: string,
  inventoryId: string,
): string {
  return `${supplierId}::${locationId}::${inventoryId}`;
}

function readCandidateStatus(
  reviewState: Record<string, CandidateReviewStatus>,
  supplierId: string,
  locationId: string,
  inventoryId: string,
): CandidateReviewStatus {
  return reviewState[candidateReviewKey(supplierId, locationId, inventoryId)] ?? 'pending';
}

function simulationTimeLabel(): string {
  return new Date().toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function readAutoOrderString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === 'string' ? value.trim() : '';
}

function supplierSupplyItems(supplier: SupplierRow | null | undefined): SupplierSupplyItem[] {
  const raw = supplier?.metadata?.supplyItems;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const items: SupplierSupplyItem[] = [];
  raw.forEach((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    const source = entry as Record<string, unknown>;
    const sourceId = readAutoOrderString(source, 'sourceId') || null;
    const sourceName = readAutoOrderString(source, 'sourceName');
    const supplierName = readAutoOrderString(source, 'supplierName') || sourceName;
    if (!sourceId || seen.has(sourceId)) return;
    seen.add(sourceId);
    items.push({
      sourceId,
      sourceName,
      supplierName,
      memo: readAutoOrderString(source, 'memo'),
      sourceLocationId: readAutoOrderString(source, 'sourceLocationId') || null,
      sourceLocationName: readAutoOrderString(source, 'sourceLocationName') || null,
      autoEnabled: source.autoEnabled === '미사용' ? '미사용' : '사용',
    });
  });
  return items;
}

function materialLocationOptions(materials: MaterialRow[]): CandidateLocation[] {
  const byId = new Map<string, CandidateLocation>();
  materials.forEach((material) => {
    if (!material.locationId) return;
    if (byId.has(material.locationId)) return;
    byId.set(material.locationId, {
      id: material.locationId,
      name: material.locationName || '사업장 미확인',
    });
  });
  return [...byId.values()];
}

function buildAutoOrderCandidateGroups(
  suppliers: SupplierRow[],
  materials: MaterialRow[],
  pendingQuantityByInventoryId: Map<string, number> = new Map(),
): AutoOrderSupplierGroup[] {
  return suppliers
    .map((supplier) => {
      const supplyItems = supplierSupplyItems(supplier).filter((item) => item.autoEnabled !== '미사용');
      const supplyItemBySourceId = new Map<string, SupplierSupplyItem>();
      supplyItems.forEach((item) => {
        if (item.sourceId && !supplyItemBySourceId.has(item.sourceId)) {
          supplyItemBySourceId.set(item.sourceId, item);
        }
      });

      const byLocation = new Map<string, AutoOrderLocationGroup>();
      materials.forEach((material) => {
        const supplyItem = supplyItemBySourceId.get(material.id);
        if (!supplyItem) return;
        const expiryState = materialExpiryState(material);
        const shortageQuantity = roundOrderQuantity(materialShortage(material));
        const pendingQuantity = roundOrderQuantity(pendingQuantityByInventoryId.get(material.id) ?? 0);
        const shouldShowLine =
          shortageQuantity > 0 || pendingQuantity > 0 || expiryState.expiryStatus !== 'none';
        if (!shouldShowLine) return;
        const quantity = roundOrderQuantity(Math.max(0, shortageQuantity - pendingQuantity));
        const locationId = material.locationId || supplyItem.sourceLocationId || 'unknown';
        const locationName =
          material.locationName || supplyItem.sourceLocationName || '사업장 미확인';
        const unit = material.comparableUnit || material.unit;
        const unitCost = Number(material.cost ?? 0);
        const line: AutoOrderCandidateLine = {
          inventoryId: material.id,
          inventoryName: supplyItem.supplierName || material.name,
          materialName: material.name,
          locationId,
          locationName,
          unit,
          shortageQuantity,
          pendingQuantity,
          quantity,
          unitCost,
          totalCost: Number.isFinite(unitCost) ? Math.round(quantity * unitCost) : 0,
          providerKind: material.providerKind,
          expiryDate: expiryState.expiryDate,
          expiryDays: expiryState.expiryDays,
          expiryStatus: expiryState.expiryStatus,
        };
        const group =
          byLocation.get(locationId) ??
          {
            locationId,
            locationName,
            lines: [],
            totalQuantity: 0,
            totalAmount: 0,
          };
        group.lines.push(line);
        group.totalQuantity += quantity;
        group.totalAmount += line.totalCost;
        byLocation.set(locationId, group);
      });

      const locationGroups = [...byLocation.values()]
        .map((group) => ({
          ...group,
          lines: group.lines.sort((a, b) => a.inventoryName.localeCompare(b.inventoryName, 'ko')),
        }))
        .sort((a, b) => a.locationName.localeCompare(b.locationName, 'ko'));

      const totalLines = locationGroups.reduce((sum, group) => sum + group.lines.length, 0);
      return {
        supplier,
        locationGroups,
        totalLines,
        totalAmount: locationGroups.reduce((sum, group) => sum + group.totalAmount, 0),
      };
    })
    .filter((group) => group.totalLines > 0)
    .sort((a, b) => a.supplier.name.localeCompare(b.supplier.name, 'ko'));
}

async function listAllAutoOrderMaterials(): Promise<MaterialsEnvelope> {
  const items: MaterialRow[] = [];
  let cursor: string | null = null;
  let count = 0;
  let limit = 100;
  for (let page = 0; page < 100; page += 1) {
    const r: Parameters<typeof unwrap<MaterialsEnvelope>>[0] =
      await inventoryControllerListMaterials({
        query: {
          limit: '100',
          ...(cursor ? { cursor } : {}),
        } as never,
      });
    const envelope: MaterialsEnvelope = unwrap<MaterialsEnvelope>(r);
    items.push(...envelope.items);
    count = envelope.count;
    limit = envelope.limit;
    if (!envelope.has_more || !envelope.cursor) {
      return { ...envelope, items, count, limit, cursor: envelope.cursor, has_more: false };
    }
    cursor = envelope.cursor;
  }
  return { items, count, limit, cursor, has_more: false };
}

function readOrderSmsSenderSettings(
  settings: Record<string, unknown> | undefined,
): OrderSmsSenderSettings {
  const raw = settings?.[ORDER_SMS_SENDER_SETTINGS_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      displayName: '',
      phone: '',
      address: '',
      businessName: '',
      businessNumber: '',
      defaultMemo: '',
    };
  }
  const source = raw as Record<string, unknown>;
  return {
    displayName: stringSetting(source.displayName),
    phone: stringSetting(source.phone),
    address: stringSetting(source.address),
    businessName: stringSetting(source.businessName),
    businessNumber: stringSetting(source.businessNumber),
    defaultMemo: stringSetting(source.defaultMemo),
  };
}

function stringSetting(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function phoneDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

function isPhoneNumberText(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return phoneDigits(trimmed).length >= 8 && /^[\d\s+().-]+$/.test(trimmed);
}

function samePhoneNumber(left: string | null | undefined, right: string | null | undefined): boolean {
  const leftDigits = phoneDigits(left);
  const rightDigits = phoneDigits(right);
  return leftDigits.length > 0 && leftDigits === rightDigits;
}

function uniqueOrderLocationIds(order: OrderRow): string[] {
  const ids = new Set<string>();
  for (const line of order.lines) {
    if (line.locationId) ids.add(line.locationId);
  }
  return [...ids];
}

function orderLocationLabel(order: OrderRow): string {
  const names = new Set<string>();
  for (const line of order.lines) {
    const name = line.locationName?.trim();
    if (name) names.add(name);
  }
  if (names.size === 0) return '사업장 미확인';
  if (names.size === 1) return [...names][0]!;
  return `여러 사업장 ${names.size}곳`;
}

function buildOrderSmsBody(
  order: OrderRow,
  lineQty: Record<string, string>,
  location: LocationRow | null,
): string {
  const sender = readOrderSmsSenderSettings(location?.settings);
  const businessName = isPhoneNumberText(sender.businessName) ? '' : sender.businessName;
  const senderName =
    sender.displayName || location?.name || businessName || '사업장 정보 미입력';
  const lines = [
    '[자동발주]',
    `발주처: ${senderName}`,
    businessName ? `상호: ${businessName}` : '',
    sender.phone ? `연락처: ${sender.phone}` : '',
    sender.address ? `주소: ${sender.address}` : '',
    sender.businessNumber ? `사업자번호: ${sender.businessNumber}` : '',
    `거래처: ${order.supplier?.name ?? '거래처 미지정'}`,
    order.expectedDate ? `납품요청일: ${order.expectedDate}` : '',
    '',
    '품목:',
    ...order.lines.map((line) => {
      const qty = lineQty[line.id] ?? line.quantity;
      const unit = line.unit ? ` ${line.unit}` : '';
      return `- ${line.inventoryName ?? '재료명 미확인'} ${formatNumber(qty)}${unit}`;
    }),
    '',
    sender.defaultMemo ? `기본 요청: ${sender.defaultMemo}` : '',
    order.notes ? `발주 메모: ${order.notes}` : '',
  ];

  return lines.filter((line) => line.trim().length > 0).join('\n');
}

function smsDraftUri(recipient: string, body: string): string {
  const normalizedRecipient = recipient.replace(/[^\d+]/g, '');
  const separator = /iPad|iPhone|iPod/.test(window.navigator.userAgent) ? '&' : '?';
  return `sms:${normalizedRecipient}${separator}body=${encodeURIComponent(body)}`;
}

function statusLabel(status: OrderRow['status']): { label: string; tone: 'default' | 'success' | 'danger' } {
  switch (status) {
    case 'draft':
      return { label: '검토 대기', tone: 'default' };
    case 'sent':
      return { label: '발송됨', tone: 'success' };
    case 'received':
      return { label: '입고 완료', tone: 'success' };
    case 'cancelled':
      return { label: '취소됨', tone: 'danger' };
    default:
      return { label: status, tone: 'default' };
  }
}

function canExcludeOrderFromPending(order: OrderRow): boolean {
  return order.status === 'draft' || order.status === 'sent';
}

function orderPendingExcludeLabel(order: OrderRow): string {
  return order.status === 'sent' ? '발주중 제외' : '발주서 삭제';
}

function orderPendingExcludePendingLabel(order: OrderRow): string {
  return order.status === 'sent' ? '제외 중...' : '삭제 중...';
}

function orderPendingExcludeConfirm(order: OrderRow): string {
  if (order.status === 'sent') {
    return '이 발송 완료 발주서를 발주중 계산에서 제외할까요? 실제 입고는 재료 화면에서 별도로 처리해야 합니다.';
  }
  return '이 발주서를 삭제하고 후보를 다시 계산할까요?';
}

function orderPendingExcludeSuccessMessage(order: OrderRow): string {
  const supplierName = order.supplier?.name ?? '발주서';
  if (order.status === 'sent') {
    return `${supplierName} 발주서를 발주중에서 제외했습니다. 후보를 다시 계산합니다.`;
  }
  return `${supplierName} 발주서를 삭제했습니다. 후보를 다시 계산합니다.`;
}

function orderPendingExcludeErrorMessage(order: OrderRow): string {
  return order.status === 'sent' ? '발주중 제외 실패' : '발주서 삭제 실패';
}

function statusFilterLabel(status: OrderStatusFilter): string {
  switch (status) {
    case 'work':
      return '처리할 발주서';
    case 'sent':
      return '발송됨';
    case 'received':
      return '입고 완료';
    case 'cancelled':
      return '취소됨';
    default:
      return status;
  }
}

const ORDER_PANEL_FILTERS: Array<{ value: OrderStatusFilter; label: string }> = [
  { value: 'work', label: '처리할 발주서' },
  { value: 'sent', label: '발송됨' },
  { value: 'received', label: '입고 완료' },
  { value: 'cancelled', label: '취소됨' },
];

const REVIEW_LINE_COLUMNS = 'minmax(140px, 1.4fr) 110px 100px 110px';
const CREATE_LINE_COLUMNS = 'minmax(140px, 1.4fr) 100px 100px 110px 32px';
const REVIEW_LINE_MIN_WIDTH = 540;
const CREATE_LINE_MIN_WIDTH = 620;
const AUTO_ORDER_REFETCH_INTERVAL_MS = 15_000;

const modalTableScrollStyle: CSSProperties = {
  overflowX: 'auto',
  maxWidth: '100%',
  WebkitOverflowScrolling: 'touch',
};

async function refreshAutoOrderQueries() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['orders'] }),
    queryClient.invalidateQueries({ queryKey: ['inventory', 'materials'] }),
    queryClient.invalidateQueries({ queryKey: ['suppliers', 'list'] }),
  ]);
}

// AO1a D10 — 발주중 수량 P 조회 (orders 접두 = refreshAutoOrderQueries 무효화 그대로).
const PENDING_ORDERS_QUERY_KEY = ['orders', 'auto-order-pending-quantity'] as const;

async function fetchPendingOrderPage(request: PendingOrderPageRequest): Promise<OrdersEnvelope> {
  const r = await ordersControllerList({
    query: {
      limit: String(request.limit),
      status: request.status,
      ...(request.cursor ? { cursor: request.cursor } : {}),
    } as never,
  });
  return unwrap<OrdersEnvelope>(r);
}

function pendingOrdersBlockMessage(reason: PendingOrdersGateReason): string {
  const state =
    reason === 'loading'
      ? '발주중 수량(작성 중·발송 발주서)을 끝까지 불러오는 중입니다.'
      : reason === 'refetching'
        ? '발주중 수량(작성 중·발송 발주서)을 다시 확인하는 중입니다.'
        : '발주중 수량(작성 중·발송 발주서)을 끝까지 불러오지 못했습니다.';
  return `${state} 완료 전에는 후보 확인·발주서 생성을 할 수 없습니다.`;
}

// ─── Route ────────────────────────────────────────────────────────────
export function AutoTab() {
  const toast = useToast();
  const [orderPanelFilter, setOrderPanelFilter] = useState<OrderStatusFilter>('work');
  const [reviewTarget, setReviewTarget] = useState<OrderRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSeed, setCreateSeed] = useState<{
    supplierId: string;
    locationId: string;
    inventoryIds?: string[];
    inventoryQuantities?: Record<string, number>;
  } | null>(null);
  const [candidateReviewState, setCandidateReviewState] = useState<Record<string, CandidateReviewStatus>>({});
  const [simulationStatus, setSimulationStatus] = useState<SimulationStatus>('idle');
  const [simulationStep, setSimulationStep] = useState(0);
  const [simulationMessages, setSimulationMessages] = useState<SimulationMessage[]>([]);
  const [simulationHistory, setSimulationHistory] = useState<SimulationHistoryEntry[]>([
    {
      id: 'initial',
      time: '대기',
      text: '자동발주 시뮬레이션 대기 - 아직 실행된 발주 이력이 없습니다.',
    },
  ]);
  const simulationTimersRef = useRef<number[]>([]);

  function clearSimulationTimers() {
    simulationTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    simulationTimersRef.current = [];
  }

  function addSimulationHistory(text: string) {
    setSimulationHistory((current) => [
      {
        id: `${Date.now()}-${current.length}`,
        time: simulationTimeLabel(),
        text,
      },
      ...current,
    ].slice(0, 12));
  }

  function startSimulation() {
    clearSimulationTimers();
    setSimulationStatus('running');
    setSimulationStep(1);
    setSimulationMessages([
      {
        id: `start-${Date.now()}`,
        kind: 'system',
        text: '예약 시간이 도달한 것으로 가정하고 자동발주 시뮬레이션을 시작합니다.',
      },
    ]);
    addSimulationHistory('자동발주 시뮬레이션 시작 - 거래처별 연결 흐름 확인');

    const nextSteps: Array<{ delay: number; step: number; text: string }> = [
      {
        delay: 450,
        step: 2,
        text: '거래처 공급물품 매핑 기준으로 후보를 거래처/사업장별로 분리했습니다.',
      },
      {
        delay: 900,
        step: 3,
        text: '확인 완료 후보를 기준으로 관리자 승인 메시지 미리보기를 만들었습니다.',
      },
      {
        delay: 1350,
        step: 4,
        text: '관리자 폰 승인 대기 상태입니다. 실제 문자, 카카오, 결제, 외부 주문은 실행하지 않습니다.',
      },
      {
        delay: 1800,
        step: 5,
        text: '승인 버튼을 선택하면 결과만 이력에 기록됩니다.',
      },
    ];

    simulationTimersRef.current = nextSteps.map(({ delay, step, text }) =>
      window.setTimeout(() => {
        setSimulationStep(step);
        setSimulationMessages((current) => [
          ...current,
          { id: `${step}-${Date.now()}`, kind: 'system', text },
        ]);
        if (step === 5) {
          setSimulationStatus('waiting');
        }
      }, delay),
    );
  }

  function resetSimulation() {
    clearSimulationTimers();
    setSimulationStatus('idle');
    setSimulationStep(0);
    setSimulationMessages([]);
    addSimulationHistory('자동발주 시뮬레이션 초기화');
  }

  function approveSimulation(action: 'approved' | 'modified' | 'skipped') {
    clearSimulationTimers();
    setSimulationStatus(action);
    setSimulationStep(5);
    const actionLabel =
      action === 'approved' ? '전체 승인' : action === 'modified' ? '수량 수정' : '오늘 제외';
    const resultText =
      action === 'approved'
        ? '승인 상태로 표시했습니다. 실제 외부 연동 없이 시뮬레이션 결과만 남깁니다.'
        : action === 'modified'
          ? '수량 수정 요청으로 표시했습니다. 실제 수량 조정은 발주서 생성 후 발주서에서 처리합니다.'
          : '오늘 발주는 제외 상태로 표시했습니다. 다음 실행 시 다시 검토하는 흐름입니다.';
    setSimulationMessages((current) => [
      ...current,
      { id: `user-${Date.now()}`, kind: 'user', text: actionLabel },
      { id: `result-${Date.now()}`, kind: 'system', text: resultText },
    ]);
    addSimulationHistory(`관리자 승인 시뮬레이션 - ${actionLabel}`);
  }

  useEffect(() => () => clearSimulationTimers(), []);

  const ordersQ = useQuery({
    queryKey: ['orders', { status: orderPanelFilter }],
    queryFn: async () => {
      const r = await ordersControllerList({
        query: {
          limit: '100',
          status: orderPanelFilter === 'work' ? 'draft' : orderPanelFilter,
        } as never,
      });
      return unwrap<OrdersEnvelope>(r);
    },
    refetchInterval: AUTO_ORDER_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const items = ordersQ.data?.items ?? [];
  const pendingOrdersQ = useQuery({
    queryKey: PENDING_ORDERS_QUERY_KEY,
    queryFn: () => loadAllPendingOrders<OrderRow>(fetchPendingOrderPage),
    refetchInterval: AUTO_ORDER_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
  const pendingGate = pendingOrdersGate(pendingOrdersQ);
  const orderHistoryQ = useQuery({
    queryKey: ['orders', 'auto-order-history'],
    queryFn: async () => {
      const statuses: Array<OrderRow['status']> = ['sent', 'received', 'cancelled'];
      const results = await Promise.all(
        statuses.map(async (status) => {
          const r = await ordersControllerList({
            query: { limit: '80', status } as never,
          });
          return unwrap<OrdersEnvelope>(r);
        }),
      );
      return results
        .flatMap((result) => result.items)
        .sort((a, b) => {
          const left = new Date(b.sentAt ?? b.receivedAt ?? b.createdAt).getTime();
          const right = new Date(a.sentAt ?? a.receivedAt ?? a.createdAt).getTime();
          return left - right;
        })
        .slice(0, 12);
    },
    refetchInterval: AUTO_ORDER_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
  const suppliersQ = useQuery({
    queryKey: ['suppliers', 'list'],
    queryFn: async () => {
      const r = await suppliersControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<SuppliersEnvelope>(r);
    },
    refetchInterval: AUTO_ORDER_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
  const suppliers = suppliersQ.data?.items ?? [];
  const materialsQ = useQuery({
    queryKey: ['inventory', 'materials', 'auto-order-waiting-list'],
    queryFn: listAllAutoOrderMaterials,
    refetchInterval: AUTO_ORDER_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
  const materials = materialsQ.data?.items ?? [];
  const pendingQuantityByInventoryId = useMemo(
    () => buildPendingQuantityByInventoryId(pendingOrdersQ.data ?? []),
    [pendingOrdersQ.data],
  );
  const candidateSupplierGroups = useMemo(
    () => buildAutoOrderCandidateGroups(suppliers, materials, pendingQuantityByInventoryId),
    [materials, pendingQuantityByInventoryId, suppliers],
  );
  useEffect(() => {
    const activeKeys = new Set<string>();
    candidateSupplierGroups.forEach((group) => {
      group.locationGroups.forEach((locationGroup) => {
        locationGroup.lines.forEach((line) => {
          activeKeys.add(candidateReviewKey(group.supplier.id, locationGroup.locationId, line.inventoryId));
        });
      });
    });
    setCandidateReviewState((current) => {
      const next: Record<string, CandidateReviewStatus> = {};
      for (const [key, value] of Object.entries(current)) {
        if (activeKeys.has(key)) next[key] = value;
      }
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [candidateSupplierGroups]);
  const visibleCandidateSupplierGroups = candidateSupplierGroups;
  const candidateLineCount = visibleCandidateSupplierGroups.reduce(
    (sum, group) => sum + group.totalLines,
    0,
  );
  const candidateReviewCounts = useMemo(() => {
    const counts = { pending: 0, confirmed: 0, excluded: 0 };
    visibleCandidateSupplierGroups.forEach((group) => {
      group.locationGroups.forEach((locationGroup) => {
        locationGroup.lines.forEach((line) => {
          if (line.quantity <= 0) return;
          counts[
            readCandidateStatus(
              candidateReviewState,
              group.supplier.id,
              locationGroup.locationId,
              line.inventoryId,
            )
          ] += 1;
        });
      });
    });
    return counts;
  }, [candidateReviewState, visibleCandidateSupplierGroups]);
  const candidateTotalAmount = visibleCandidateSupplierGroups.reduce(
    (sum, group) => sum + group.totalAmount,
    0,
  );
  const totalAmount = items.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0);
  const cancelOrderMu = useMutation({
    mutationFn: async (order: OrderRow) => {
      const r = await ordersControllerRemove({ path: { id: order.id } });
      return { order, result: unwrap<{ deleted: boolean; id: string }>(r) };
    },
    onSuccess: async ({ order }) => {
      setCandidateReviewState({});
      toast.success(orderPendingExcludeSuccessMessage(order));
      await refreshAutoOrderQueries();
    },
    onError: (e, order) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? orderPendingExcludeErrorMessage(order));
    },
  });
  const candidateRefreshing =
    materialsQ.isFetching || pendingOrdersQ.isFetching || suppliersQ.isFetching || cancelOrderMu.isPending;
  // 첫 로딩·오류(stale cache 포함) 안내 — 백그라운드 재조회 중에는 버튼만 막는다 (15초 주기 깜빡임 방지).
  const pendingGateNotice =
    pendingGate.reason === 'error' ? (
      <AlertBanner
        tone="danger"
        message={`${pendingOrdersBlockMessage('error')} (${
          (pendingOrdersQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
        })`}
        cta={pendingOrdersQ.isFetching ? '다시 불러오는 중…' : '다시 시도'}
        onCta={pendingOrdersQ.isFetching ? undefined : () => void pendingOrdersQ.refetch()}
      />
    ) : pendingGate.reason === 'loading' ? (
      <AlertBanner tone="info" message={pendingOrdersBlockMessage('loading')} />
    ) : null;

  return (
    <div className="dvvb-auto-root">
      <section className="dvvb-auto-main">
        <header className="dvvb-waitlist-hero">
          <div>
            <div className="dvvb-eyebrow">SUPPLIER ITEMS ONLY</div>
            <h2 className="dvvb-display">자동발주 대기 리스트</h2>
            <div className="dvvb-copy" style={{ marginTop: 8, maxWidth: 780 }}>
              재고 부족 항목이 넘어오면 거래처 탭의 공급 물품 매핑을 기준으로 업체별 발주 초안을 확인합니다.
              발주 실행은 발주서 검토 모달과 문자앱 열기 흐름으로 연결됩니다.
            </div>
          </div>
          <div className="dvvb-metric-grid">
            <Metric label={`후보 ${candidateLineCount}개 품목`} value={candidateLineCount} sub="거래처 공급물품 매핑 기준" />
            <Metric label={`거래처 ${visibleCandidateSupplierGroups.length}곳`} value={visibleCandidateSupplierGroups.length} sub="거래처별 대기 카드" />
            <Metric label={`발주서 ${items.length}건`} value={items.length} sub={statusFilterLabel(orderPanelFilter)} />
            <Metric label="발주서 합계" value={formatWon(totalAmount || candidateTotalAmount)} sub="선택된 발주서 상태 기준" />
          </div>
        </header>

        <div className="dvvb-banner">
          <Icon name="alert" size={16} color="var(--info)" />
          <div>
            푸시가 들어온 물품은 거래처 페이지의 공급 물품 매핑을 기준으로 업체를 찾고, 업체별 후보 카드로 분리해서 검토합니다.
            후보 카드에서 확인/제외를 마친 품목만 발주서로 생성하고, 수량 조정은 발주서에서 처리합니다.
          </div>
        </div>

        <div className="dvvb-auto-workspace">
          <section className="dvvb-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="dvvb-panel-head">
              <div className="dvvb-section-head">
                <div>
                  <h3 className="dvvb-title" style={{ fontSize: 18 }}>
                    거래처별 자동발주 대기 리스트
                  </h3>
                  <div className="dvvb-copy" style={{ marginTop: 4 }}>
                    현재재고, 안전재고, 자동 계산 수량을 검토하고 거래처/사업장별로 확인 완료한 품목만 발주서로 넘깁니다.
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    await refreshAutoOrderQueries();
                    toast.success('자동발주 후보를 다시 계산했습니다.');
                  }}
                  disabled={candidateRefreshing}
                >
                  <Icon name="refresh" size={14} />
                  후보 새로고침
                </Button>
              </div>
            </div>

            {pendingGateNotice &&
              !materialsQ.isLoading &&
              !suppliersQ.isLoading &&
              !materialsQ.isError &&
              !suppliersQ.isError && (
                // 후보 카드 grid 밖 — 카드가 패널보다 넓어도 안내·재시도 버튼이 잘리지 않게.
                <div style={{ padding: '16px 16px 0', minWidth: 0 }}>{pendingGateNotice}</div>
              )}
            <div style={{ padding: 16, display: 'grid', gap: 12 }}>
              {materialsQ.isLoading || suppliersQ.isLoading ? (
                <div className="dvvb-copy">거래처별 자동발주 후보를 계산하는 중...</div>
              ) : materialsQ.isError || suppliersQ.isError ? (
                <AlertBanner
                  tone="danger"
                  message={`자동발주 후보를 계산하지 못했습니다: ${
                    ((materialsQ.error ?? suppliersQ.error) as ApiCallError | undefined)?.message ??
                    '알 수 없는 오류'
                  }`}
                />
              ) : pendingOrdersQ.data === undefined ? null : visibleCandidateSupplierGroups.length === 0 ? (
                <EmptyState
                  icon="truck"
                  title="자동발주 후보가 없습니다"
                  description="거래처 탭에서 공급 물품을 등록하고 부족재고가 발생하면 업체별 후보 카드가 표시됩니다."
                />
              ) : (
                visibleCandidateSupplierGroups.map((group) => (
                  <AutoOrderCandidateSupplierCard
                    key={group.supplier.id}
                    group={group}
                    reviewState={candidateReviewState}
                    blockReason={pendingGate.reason}
                    onSetLineStatus={(supplierId, locationId, inventoryId, status) => {
                      if (status === 'confirmed' && !pendingGate.ready) return;
                      const key = candidateReviewKey(supplierId, locationId, inventoryId);
                      setCandidateReviewState((current) => ({
                        ...current,
                        [key]: status,
                      }));
                      addSimulationHistory(
                        `자동발주 후보 품목 ${CANDIDATE_REVIEW_LABEL[status]} 처리`,
                      );
                    }}
                    onConfirmLocation={(supplierId, locationId, inventoryIds) => {
                      if (!pendingGate.ready) return;
                      setCandidateReviewState((current) => {
                        const next = { ...current };
                        inventoryIds.forEach((inventoryId) => {
                          const key = candidateReviewKey(supplierId, locationId, inventoryId);
                          next[key] = 'confirmed';
                        });
                        return next;
                      });
                      addSimulationHistory('사업장 대기 리스트 확인 완료 - 발주서 생성 가능 상태 확인');
                    }}
                    onCreateLocationDraft={(supplierId, locationId, lines) => {
                      if (!pendingGate.ready) return;
                      setCreateSeed({
                        supplierId,
                        locationId,
                        inventoryIds: lines.map((line) => line.inventoryId),
                        inventoryQuantities: Object.fromEntries(
                          lines.map((line) => [line.inventoryId, line.quantity]),
                        ),
                      });
                      setCreateOpen(true);
                    }}
                  />
                ))
              )}
            </div>
          </section>

          <section className="dvvb-card dvvb-order-panel" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="dvvb-panel-head">
              <div className="dvvb-section-head">
                <div>
                  <h3 className="dvvb-title" style={{ fontSize: 18 }}>
                    {orderPanelFilter === 'work' ? '처리할 발주서' : '발주 히스토리'}
                  </h3>
                  <div className="dvvb-copy" style={{ marginTop: 4 }}>
                    작성된 발주서는 여기에서 품목 내역까지 펼쳐서 확인합니다.
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => {
                    setCreateSeed(null);
                    setCreateOpen(true);
                  }}
                >
                  <Icon name="plus" size={14} />
                  수동 발주서 작성
                </Button>
              </div>
            </div>

            <div className="dvvb-order-tabs">
              {ORDER_PANEL_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  className={`dvvb-order-tab${orderPanelFilter === filter.value ? ' is-active' : ''}`}
                  onClick={() => setOrderPanelFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="dvvb-order-panel-body">
              {ordersQ.isLoading ? (
                <div className="dvvb-copy">발주서 목록을 불러오는 중...</div>
              ) : ordersQ.isError ? (
                <AlertBanner
                  tone="danger"
                  message={`발주서를 불러오지 못했습니다: ${
                    (ordersQ.error as ApiCallError | undefined)?.message ?? '알 수 없는 오류'
                  }`}
                />
              ) : items.length === 0 ? (
                <EmptyState
                  icon="truck"
                  title={orderPanelFilter === 'work' ? '처리할 발주서가 없습니다' : '히스토리가 없습니다'}
                  description={
                    orderPanelFilter === 'work'
                      ? '왼쪽 후보 카드에서 확인 완료 후 발주서를 생성하면 이 영역에 펼쳐서 표시됩니다.'
                      : '발송, 입고 완료, 취소된 발주서가 이 영역에 표시됩니다.'
                  }
                />
              ) : (
                items.map((order) => {
                  const canExclude = canExcludeOrderFromPending(order);
                  return (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onReview={() => setReviewTarget(order)}
                      onCancel={
                        canExclude
                          ? () => {
                              if (window.confirm(orderPendingExcludeConfirm(order))) {
                                cancelOrderMu.mutate(order);
                              }
                            }
                          : undefined
                      }
                      cancelPending={cancelOrderMu.isPending && cancelOrderMu.variables?.id === order.id}
                    />
                  );
                })
              )}
            </div>
          </section>
        </div>

        <PersistedOrderHistoryPanel
          orders={orderHistoryQ.data ?? []}
          isLoading={orderHistoryQ.isLoading}
          isError={orderHistoryQ.isError}
          error={orderHistoryQ.error as ApiCallError | null}
          onReview={(order) => setReviewTarget(order)}
          onCancel={(order) => cancelOrderMu.mutate(order)}
          cancelingOrderId={cancelOrderMu.isPending ? cancelOrderMu.variables?.id ?? null : null}
        />

        <AutoOrderSimulationPanel
          status={simulationStatus}
          step={simulationStep}
          messages={simulationMessages}
          history={simulationHistory}
          candidateLineCount={candidateLineCount}
          confirmedCount={candidateReviewCounts.confirmed}
          excludedCount={candidateReviewCounts.excluded}
          supplierCount={visibleCandidateSupplierGroups.length}
          totalAmount={candidateTotalAmount}
          onStart={startSimulation}
          onReset={resetSimulation}
          onApprove={approveSimulation}
        />
      </section>

      <OrderReviewModal
        order={reviewTarget}
        onClose={() => setReviewTarget(null)}
      />
      <OrderCreateModal
        open={createOpen}
        initialSupplierId={createSeed?.supplierId ?? ''}
        initialLocationId={createSeed?.locationId ?? ''}
        initialInventoryIds={createSeed?.inventoryIds}
        initialInventoryQuantities={createSeed?.inventoryQuantities}
        candidateBlockReason={pendingGate.reason}
        onClose={() => {
          setCreateOpen(false);
          setCreateSeed(null);
        }}
      />
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub: string;
}) {
  return (
    <div className="dvvb-metric">
      <div className="dvvb-metric-label">{label}</div>
      <div className="dvvb-metric-value">{value}</div>
      <div className="dvvb-metric-sub">{sub}</div>
    </div>
  );
}

function AutoOrderCandidateSupplierCard({
  group,
  reviewState,
  blockReason,
  onSetLineStatus,
  onConfirmLocation,
  onCreateLocationDraft,
}: {
  group: AutoOrderSupplierGroup;
  reviewState: Record<string, CandidateReviewStatus>;
  /** 발주중 수량 조회가 완료되지 않은 이유 — 있으면 후보 확인·발주서 생성 차단. */
  blockReason: PendingOrdersGateReason | null;
  onSetLineStatus: (
    supplierId: string,
    locationId: string,
    inventoryId: string,
    status: CandidateReviewStatus,
  ) => void;
  onConfirmLocation: (supplierId: string, locationId: string, inventoryIds: string[]) => void;
  onCreateLocationDraft: (
    supplierId: string,
    locationId: string,
    lines: AutoOrderCandidateLine[],
  ) => void;
}) {
  const blockTitle = blockReason ? pendingOrdersBlockMessage(blockReason) : undefined;
  return (
    <article className="dvvb-candidate-card">
      <div className="dvvb-section-head" style={{ marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="dvvb-candidate-title">{group.supplier.name}</div>
          <div className="dvvb-copy" style={{ marginTop: 4 }}>
            거래처 공급물품 매핑으로 계산된 자동발주 후보입니다. 실행/입고 기록은 사업장별로 분리합니다.
          </div>
        </div>
        <div className="dvvb-candidate-badges">
          <Badge variant="default">후보 {group.totalLines}개</Badge>
          <Badge variant="default">사업장 {group.locationGroups.length}개</Badge>
        </div>
      </div>

      <div className="dvvb-candidate-summary">
        <span>{group.supplier.autoOrderEnabled ? '자동발주 사용' : '자동발주 미사용'}</span>
        {typeof group.supplier.defaultLeadTimeDays === 'number' && (
          <span>기본 리드타임 {group.supplier.defaultLeadTimeDays}일</span>
        )}
        <span>사업장별 발주 묶음 {group.locationGroups.length}개</span>
        <span>예상 합계 {formatWon(group.totalAmount)}</span>
        {group.supplier.smsRecipient && <span>문자 {group.supplier.smsRecipient}</span>}
      </div>

      <div className="dvvb-location-group-list">
        {group.locationGroups.map((locationGroup) => {
          const lineStatuses = locationGroup.lines.map((line) =>
            readCandidateStatus(reviewState, group.supplier.id, locationGroup.locationId, line.inventoryId),
          );
          const confirmedLines = locationGroup.lines.filter(
            (line) =>
              line.quantity > 0 &&
              readCandidateStatus(reviewState, group.supplier.id, locationGroup.locationId, line.inventoryId) ===
              'confirmed',
          );
          const reviewableLines = locationGroup.lines.filter((line) => line.quantity > 0);
          const coveredCount = locationGroup.lines.filter((line) => line.quantity <= 0 && line.pendingQuantity > 0).length;
          const attentionCount = locationGroup.lines.filter(
            (line) => line.quantity <= 0 && line.pendingQuantity <= 0 && line.expiryStatus !== 'none',
          ).length;
          const excludedCount = lineStatuses.filter((status) => status === 'excluded').length;
          const pendingCount = reviewableLines.filter(
            (line) =>
              readCandidateStatus(reviewState, group.supplier.id, locationGroup.locationId, line.inventoryId) ===
              'pending',
          ).length;
          const canCreateOrder = pendingCount === 0 && confirmedLines.length > 0;

          return (
            <section key={locationGroup.locationId} className="dvvb-location-group">
              <div className="dvvb-location-group-head">
                <div>
                  <div className="dvvb-location-title">{locationGroup.locationName}</div>
                  <div className="dvvb-copy" style={{ marginTop: 3 }}>
                    이 사업장 재고로 입고 처리될 발주 묶음입니다. 카드에서는 확인/제외만 처리하고 수량은 발주서에서 조정합니다.
                  </div>
                </div>
                <div className="dvvb-location-actions">
                  <span className="dvvb-pill">{locationGroup.lines.length}개 품목</span>
                  {coveredCount > 0 && <span className="dvvb-pill">발주중 {coveredCount}</span>}
                  {attentionCount > 0 && <span className="dvvb-pill">확인필요 {attentionCount}</span>}
                  <span className="dvvb-pill is-good">확인 {confirmedLines.length}</span>
                  {excludedCount > 0 && <span className="dvvb-pill is-bad">제외 {excludedCount}</span>}
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={reviewableLines.length === 0 || blockReason != null}
                    title={blockTitle}
                    onClick={() =>
                      onConfirmLocation(
                        group.supplier.id,
                        locationGroup.locationId,
                        reviewableLines.map((line) => line.inventoryId),
                      )
                    }
                  >
                    이 사업장 대기 리스트 확인 완료
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!canCreateOrder || blockReason != null}
                    title={blockTitle}
                    onClick={() =>
                      onCreateLocationDraft(
                        group.supplier.id,
                        locationGroup.locationId,
                        confirmedLines,
                      )
                    }
                  >
                    발주서 생성
                  </Button>
                </div>
              </div>

              <div className="dvvb-candidate-lines">
                {locationGroup.lines.map((line) => {
                  const status = readCandidateStatus(
                    reviewState,
                    group.supplier.id,
                    locationGroup.locationId,
                    line.inventoryId,
                  );
                  const isCoveredByPending = line.quantity <= 0 && line.pendingQuantity > 0;
                  const isAttentionOnly =
                    line.quantity <= 0 && line.pendingQuantity <= 0 && line.expiryStatus !== 'none';
                  const isReviewDisabled = line.quantity <= 0;
                  const stateLabel = autoOrderLineStateLabel(line);
                  const lineNote = autoOrderLineNote(line);
                  return (
                    <div
                      key={line.inventoryId}
                      className={`dvvb-candidate-line is-${
                        isCoveredByPending ? 'covered' : isAttentionOnly ? 'attention' : status
                      }`}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div className="dvvb-candidate-line-name">
                          {line.inventoryName}
                          {line.unit && <span>({line.unit})</span>}
                        </div>
                        <div className="dvvb-copy" style={{ marginTop: 2 }}>
                          내부 재료명 {line.materialName} · {line.providerKind === 'boxhero' ? 'BoxHero 연결' : '직접 등록'}
                          {line.expiryDate ? ` · 유통기한 ${line.expiryDate}` : ''}
                        </div>
                        {lineNote && (
                          <div className="dvvb-line-note">
                            {lineNote}
                          </div>
                        )}
                      </div>
                      <div className="dvvb-stock-cell">
                        <span className="dvvb-stock-label">현재 필요</span>
                        <span className="dvvb-stock-value">
                          {formatNumber(line.shortageQuantity)} {line.unit}
                        </span>
                      </div>
                      <div className="dvvb-stock-cell">
                        <span className="dvvb-stock-label">발주중</span>
                        <span className="dvvb-stock-value">
                          {formatNumber(line.pendingQuantity)} {line.unit}
                        </span>
                      </div>
                      <div className="dvvb-stock-cell">
                        <span className="dvvb-stock-label">추가 발주</span>
                        <span className="dvvb-stock-value">
                          {formatNumber(line.quantity)} {line.unit}
                        </span>
                      </div>
                      <div className="dvvb-stock-cell">
                        <span className="dvvb-stock-label">합계</span>
                        <span className="dvvb-stock-value">{formatWon(line.totalCost)}</span>
                      </div>
                      <div className="dvvb-candidate-review-actions">
                        <span className={`dvvb-pill${status === 'confirmed' ? ' is-good' : status === 'excluded' ? ' is-bad' : ''}`}>
                          {stateLabel || CANDIDATE_REVIEW_LABEL[status]}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isReviewDisabled || status === 'confirmed' || blockReason != null}
                          title={blockTitle}
                          onClick={() =>
                            onSetLineStatus(
                              group.supplier.id,
                              locationGroup.locationId,
                              line.inventoryId,
                              'confirmed',
                            )
                          }
                        >
                          확인
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isReviewDisabled || status === 'excluded'}
                          onClick={() =>
                            onSetLineStatus(
                              group.supplier.id,
                              locationGroup.locationId,
                              line.inventoryId,
                              'excluded',
                            )
                          }
                          style={{ color: 'var(--danger)' }}
                        >
                          제외
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {blockReason === 'error' ? (
                <div className="dvvb-candidate-gate-note">{pendingOrdersBlockMessage('error')}</div>
              ) : !canCreateOrder && (
                <div className="dvvb-candidate-gate-note">
                  {reviewableLines.length === 0
                    ? attentionCount > 0
                      ? '유통기한 확인이 필요한 품목입니다. 재료에서 사용/폐기/재고조정 후 다시 계산하세요.'
                      : '발주중 수량을 제외하면 추가 발주할 품목이 없습니다.'
                    : confirmedLines.length === 0 && pendingCount === 0
                    ? '모든 품목이 제외되어 발주서로 넘길 품목이 없습니다.'
                    : `발주서 생성 전 ${pendingCount}개 품목을 확인하거나 제외하세요.`}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </article>
  );
}

function PersistedOrderHistoryPanel({
  orders,
  isLoading,
  isError,
  error,
  onReview,
  onCancel,
  cancelingOrderId,
}: {
  orders: OrderRow[];
  isLoading: boolean;
  isError: boolean;
  error: ApiCallError | null;
  onReview?: (order: OrderRow) => void;
  onCancel?: (order: OrderRow) => void;
  cancelingOrderId?: string | null;
}) {
  return (
    <section className="dvvb-card dvvb-persisted-history">
      <div className="dvvb-panel-head">
        <div className="dvvb-section-head">
          <div>
            <h3 className="dvvb-title" style={{ fontSize: 18 }}>
              실제 발주 히스토리
            </h3>
            <div className="dvvb-copy" style={{ marginTop: 4 }}>
              발송됨, 입고 완료, 취소된 발주서를 사업장 기준으로 확인합니다.
            </div>
          </div>
          <span className="dvvb-pill">최근 {orders.length}건</span>
        </div>
      </div>
      <div className="dvvb-history-list">
        {isLoading ? (
          <div className="dvvb-copy">발주 히스토리를 불러오는 중...</div>
        ) : isError ? (
          <AlertBanner
            tone="danger"
            message={`발주 히스토리를 불러오지 못했습니다: ${error?.message ?? '알 수 없는 오류'}`}
          />
        ) : orders.length === 0 ? (
          <EmptyState
            icon="truck"
            title="발주 히스토리가 없습니다"
            description="발송 완료, 입고 완료, 취소된 발주서가 여기에 표시됩니다."
          />
        ) : (
          orders.map((order) => {
            const status = statusLabel(order.status);
            const eventDate = order.sentAt ?? order.receivedAt ?? order.createdAt;
            const canExclude = canExcludeOrderFromPending(order);
            const isCanceling = cancelingOrderId === order.id;
            return (
              <div
                key={order.id}
                className="dvvb-order-history-row"
              >
                <div style={{ minWidth: 0 }}>
                  <div className="dvvb-order-history-title">
                    {order.supplier?.name ?? '(거래처 없음)'}
                    <span>사업장: {orderLocationLabel(order)}</span>
                  </div>
                  <div className="dvvb-copy" style={{ marginTop: 3 }}>
                    {new Date(eventDate).toLocaleString('ko-KR')} · 품목 {order.lines.length}개
                  </div>
                </div>
                <div className="dvvb-order-history-meta">
                  <Badge variant={status.tone}>{status.label}</Badge>
                  <strong>{formatWon(order.totalAmount)}</strong>
                  {(onReview || (canExclude && onCancel)) && (
                    <div className="dvvb-order-history-actions">
                      {onReview && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onReview(order)}
                        >
                          상세
                        </Button>
                      )}
                      {canExclude && onCancel && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCancel(order)}
                          disabled={isCanceling}
                          style={{ color: 'var(--danger)' }}
                        >
                          {isCanceling
                            ? orderPendingExcludePendingLabel(order)
                            : orderPendingExcludeLabel(order)}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function AutoOrderSimulationPanel({
  status,
  step,
  messages,
  history,
  candidateLineCount,
  confirmedCount,
  excludedCount,
  supplierCount,
  totalAmount,
  onStart,
  onReset,
  onApprove,
}: {
  status: SimulationStatus;
  step: number;
  messages: SimulationMessage[];
  history: SimulationHistoryEntry[];
  candidateLineCount: number;
  confirmedCount: number;
  excludedCount: number;
  supplierCount: number;
  totalAmount: number;
  onStart: () => void;
  onReset: () => void;
  onApprove: (action: 'approved' | 'modified' | 'skipped') => void;
}) {
  const canApprove = status === 'waiting';
  return (
    <section className="dvvb-simulation-grid">
      <article className="dvvb-card dvvb-simulation-card">
        <div className="dvvb-panel-head">
          <div className="dvvb-section-head">
            <div>
              <h3 className="dvvb-title" style={{ fontSize: 18 }}>
                실행 시뮬레이션
              </h3>
              <div className="dvvb-copy" style={{ marginTop: 4 }}>
                실제 문자, 카카오, 결제, 외부 주문 없이 자동발주 실행 흐름만 검증합니다.
              </div>
            </div>
            <div className="dvvb-location-actions">
              <span className="dvvb-pill">{SIMULATION_STATUS_LABEL[status]}</span>
              <Button
                variant="primary"
                size="sm"
                disabled={status === 'running' || candidateLineCount === 0}
                onClick={onStart}
              >
                시뮬레이션 시작
              </Button>
              <Button variant="secondary" size="sm" onClick={onReset}>
                처음부터
              </Button>
            </div>
          </div>
        </div>

        <div className="dvvb-simulation-body">
          <div className="dvvb-simulation-summary">
            <span>거래처 {supplierCount}곳</span>
            <span>후보 {candidateLineCount}개</span>
            <span>확인 {confirmedCount}개</span>
            <span>제외 {excludedCount}개</span>
            <span>{formatWon(totalAmount)}</span>
          </div>
          <div className="dvvb-timeline">
            {SIMULATION_TIMELINE.map((item, index) => {
              const stepNumber = index + 1;
              return (
                <div
                  key={item.title}
                  className={`dvvb-timeline-step${step >= stepNumber ? ' is-active' : ''}`}
                >
                  <div className="dvvb-timeline-num">{stepNumber}</div>
                  <div>
                    <div className="dvvb-timeline-title">{item.title}</div>
                    <div className="dvvb-copy" style={{ marginTop: 3 }}>
                      {item.detail}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </article>

      <article className="dvvb-card dvvb-phone-card">
        <div className="dvvb-phone-head">
          <div>
            <div className="dvvb-eyebrow">PHONE PREVIEW</div>
            <div className="dvvb-h3" style={{ marginTop: 3 }}>
              관리자 승인 미리보기
            </div>
          </div>
          <Icon name="message" size={18} color="var(--primary-strong)" />
        </div>
        <div className="dvvb-phone-screen">
          {messages.length === 0 ? (
            <div className="dvvb-phone-bubble is-system">
              미리보기 대기 중입니다. 시뮬레이션 시작을 누르면 승인 메시지가 채워집니다.
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`dvvb-phone-bubble${message.kind === 'user' ? ' is-user' : ' is-system'}`}
              >
                {message.text}
              </div>
            ))
          )}
        </div>
        <div className="dvvb-phone-actions">
          <Button variant="secondary" size="sm" disabled={!canApprove} onClick={() => onApprove('approved')}>
            전체 승인
          </Button>
          <Button variant="secondary" size="sm" disabled={!canApprove} onClick={() => onApprove('modified')}>
            수량 수정
          </Button>
          <Button variant="ghost" size="sm" disabled={!canApprove} onClick={() => onApprove('skipped')}>
            오늘 제외
          </Button>
        </div>
      </article>

      <article className="dvvb-card dvvb-history-card">
        <div className="dvvb-panel-head">
          <div>
            <h3 className="dvvb-title" style={{ fontSize: 18 }}>
              시뮬레이션 이력
            </h3>
            <div className="dvvb-copy" style={{ marginTop: 4 }}>
              대기 리스트 확인, 제외, 승인 시뮬레이션 결과를 기록합니다.
            </div>
          </div>
        </div>
        <div className="dvvb-history-list">
          {history.map((entry) => (
            <div key={entry.id} className="dvvb-history-item">
              <span>{entry.time}</span>
              <strong>{entry.text}</strong>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
}

// ─── OrderCard ────────────────────────────────────────────────────────
function OrderCard({
  order,
  onReview,
  onCancel,
  cancelPending = false,
}: {
  order: OrderRow;
  onReview: () => void;
  onCancel?: () => void;
  cancelPending?: boolean;
}) {
  const status = statusLabel(order.status);
  const isMobile = useViewport() === 'mobile';
  const locationLabel = orderLocationLabel(order);
  return (
    <Card style={{ padding: 0, minWidth: 0 }}>
      <div
        style={{
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          borderBottom: '1px solid var(--border-weak)',
          background: 'var(--bg-subtle)',
          minWidth: 0,
        }}
      >
        <Icon name="truck" size={16} color="var(--primary-strong)" />
        <div
          style={{
            flex: '1 1 160px',
            minWidth: 0,
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--fg-1)',
            display: 'grid',
            gap: 3,
            overflow: 'hidden',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {order.supplier?.name ?? '(거래처 없음)'}
          </span>
          <span
            style={{
              color: locationLabel === '사업장 미확인' ? 'var(--danger)' : 'var(--fg-3)',
              fontSize: 12,
              fontWeight: 800,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            사업장: {locationLabel}
          </span>
        </div>
        <Badge variant={status.tone}>{status.label}</Badge>
        {order.isAutoGenerated && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 999,
              color: 'var(--primary-strong)',
              background: 'var(--primary-bg)',
            }}
          >
            자동 생성
          </span>
        )}
        <div
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: '1 1 180px',
            justifyContent: isMobile ? 'flex-start' : 'flex-end',
            flexWrap: 'wrap',
            minWidth: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>품목 {order.lines.length}</span>
          <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>
            {formatWon(order.totalAmount)}
          </span>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={onReview}
          style={isMobile ? { width: '100%' } : undefined}
        >
          {order.status === 'draft' ? '검토 / 발송' : '상세'}
        </Button>
        {canExcludeOrderFromPending(order) && onCancel && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={cancelPending}
            style={{
              color: 'var(--danger)',
              ...(isMobile ? { width: '100%' } : undefined),
            }}
          >
            <Icon name="trash" size={13} />
            {cancelPending
              ? orderPendingExcludePendingLabel(order)
              : orderPendingExcludeLabel(order)}
          </Button>
        )}
      </div>

      <div style={{ padding: '10px 14px', minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            color: 'var(--fg-3)',
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <span>생성: {new Date(order.createdAt).toLocaleString('ko-KR')}</span>
          <span>사업장: {locationLabel}</span>
          {order.sentAt && (
            <span>발송: {new Date(order.sentAt).toLocaleString('ko-KR')}</span>
          )}
          {order.expectedDate && <span>예상 입고: {order.expectedDate}</span>}
          {order.notes && (
            <span style={{ color: 'var(--fg-2)' }}>· {order.notes}</span>
          )}
        </div>

        <div className="dvvb-order-line-preview">
          <div className="dvvb-order-line-preview-head">
            <span>품목</span>
            <span>수량</span>
            <span>합계</span>
          </div>
          {order.lines.map((line) => (
            <div key={line.id} className="dvvb-order-line-preview-row">
              <div style={{ minWidth: 0 }}>
                <div className="dvvb-order-line-name">
                  {line.inventoryName ?? '(재료 없음)'}
                </div>
                <div className="dvvb-copy" style={{ marginTop: 2 }}>
                  {line.locationName ?? '사업장 미확인'}
                  {line.unit ? ` · ${line.unit}` : ''}
                </div>
              </div>
              <div className="dvvb-order-line-num">
                {formatNumber(line.quantity)}
                {line.unit ? ` ${line.unit}` : ''}
              </div>
              <div className="dvvb-order-line-num">{formatWon(line.totalCost)}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ─── OrderReviewModal — draft 편집 (수량) + 발송 + 취소 ────────────────
function OrderReviewModal({
  order,
  onClose,
}: {
  order: OrderRow | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [lineQty, setLineQty] = useState<Record<string, string>>({});
  const [coupangMcpPreparedAt, setCoupangMcpPreparedAt] = useState<string | null>(null);
  const [coupangRunnerJobId, setCoupangRunnerJobId] = useState<string | null>(null);
  const [coupangRunnerJob, setCoupangRunnerJob] = useState<OnlineOrderRunnerJobRecord | null>(null);
  const [coupangRunnerBusy, setCoupangRunnerBusy] = useState(false);
  const [coupangRunnerPolling, setCoupangRunnerPolling] = useState(false);
  const [coupangRunnerError, setCoupangRunnerError] = useState<string | null>(null);
  const locationsQ = useQuery({
    queryKey: ['locations', 'list'],
    queryFn: async () => {
      const r = await locationsControllerList({
        query: { limit: '200' } as never,
      });
      return unwrap<LocationsEnvelope>(r);
    },
    enabled: !!order,
  });
  const suppliersQ = useQuery({
    queryKey: ['suppliers', 'list'],
    queryFn: async () => {
      const r = await suppliersControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<SuppliersEnvelope>(r);
    },
    enabled: !!order,
  });

  useEffect(() => {
    if (!order) return;
    const m: Record<string, string> = {};
    for (const l of order.lines) {
      m[l.id] = String(Math.round(Number(l.quantity) * 10000) / 10000);
    }
    setLineQty(m);
    setCoupangMcpPreparedAt(null);
    setCoupangRunnerJobId(null);
    setCoupangRunnerJob(null);
    setCoupangRunnerBusy(false);
    setCoupangRunnerPolling(false);
    setCoupangRunnerError(null);
  }, [order]);

  useEffect(() => {
    if (!coupangRunnerJobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      if (!coupangRunnerJobId) return;
      setCoupangRunnerPolling(true);
      try {
        const result = await callDvvbApi<OnlineOrderRunnerJobResponse>(
          `/v1/online-order-runner/jobs/${encodeURIComponent(coupangRunnerJobId)}`,
        );
        if (cancelled) return;
        setCoupangRunnerJob(result.item);
        setCoupangRunnerError(null);
        if (!isOnlineOrderRunnerTerminalStatus(result.item.status)) {
          timer = setTimeout(poll, 2500);
        }
      } catch (e) {
        if (cancelled) return;
        const err = e as ApiCallError;
        setCoupangRunnerError(err.message ?? '쿠팡 MCP 상태 조회 실패');
        timer = setTimeout(poll, 5000);
      } finally {
        if (!cancelled) {
          setCoupangRunnerPolling(false);
        }
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [coupangRunnerJobId]);

  function buildLineUpdatePayload() {
    if (!order) return [];
    return order.lines
      .map((line) => {
        const quantity = Number(lineQty[line.id] ?? line.quantity);
        const unitCost = Number(line.unitCost);
        return {
          inventoryId: line.inventoryId,
          quantity,
          unitCost,
        };
      })
      .filter((line) => Number.isFinite(line.quantity) && line.quantity > 0 && Number.isFinite(line.unitCost));
  }

  const sendMu = useMutation({
    mutationFn: async () => {
      if (!order) throw new Error('order missing');
      const r = await ordersControllerUpdate({
        path: { id: order.id },
        body: { status: 'sent', lines: buildLineUpdatePayload() } as never,
      });
      return unwrap<{ item: OrderRow }>(r);
    },
    onSuccess: async () => {
      toast.success('발주서를 발송 완료 상태로 변경했습니다.');
      await refreshAutoOrderQueries();
      onClose();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '발송 실패');
    },
  });

  const saveMu = useMutation({
    mutationFn: async () => {
      if (!order) throw new Error('order missing');
      const r = await ordersControllerUpdate({
        path: { id: order.id },
        body: { lines: buildLineUpdatePayload() } as never,
      });
      return unwrap<{ item: OrderRow }>(r);
    },
    onSuccess: async () => {
      toast.success('발주서 수량을 저장했습니다.');
      await refreshAutoOrderQueries();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '수량 저장 실패');
    },
  });

  const cancelMu = useMutation({
    mutationFn: async () => {
      if (!order) throw new Error('order missing');
      const r = await ordersControllerRemove({ path: { id: order.id } });
      return unwrap<{ deleted: boolean; id: string }>(r);
    },
    onSuccess: async () => {
      toast.success(order ? orderPendingExcludeSuccessMessage(order) : '발주서를 취소했습니다.');
      await refreshAutoOrderQueries();
      onClose();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? (order ? orderPendingExcludeErrorMessage(order) : '취소 실패'));
    },
  });

  const writebackMu = useMutation({
    mutationFn: async () => {
      if (!coupangRunnerJobId) throw new Error('runner job missing');
      return callDvvbApi<OnlineOrderRunnerWritebackResponse>(
        `/v1/online-order-runner/jobs/${encodeURIComponent(coupangRunnerJobId)}/writeback`,
        { method: 'POST' },
      );
    },
    onSuccess: async () => {
      toast.success('쿠팡 MCP 결과를 발송 완료 상태로 반영했습니다.');
      await refreshAutoOrderQueries();
      onClose();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? 'MCP 결과 반영 실패');
    },
  });

  if (!order) {
    return (
      <Modal open={false} onClose={onClose} title="발주서 검토">
        <div />
      </Modal>
    );
  }
  const activeOrder = order;

  const totalAmount = activeOrder.lines.reduce((acc, l) => {
    const q = Number(lineQty[l.id] ?? l.quantity);
    const u = Number(l.unitCost);
    return acc + (Number.isFinite(q) && Number.isFinite(u) ? Math.round(q * u) : 0);
  }, 0);

  const isDraft = activeOrder.status === 'draft';
  const canExcludeFromPending = canExcludeOrderFromPending(activeOrder);
  const status = statusLabel(activeOrder.status);
  const locationIds = uniqueOrderLocationIds(activeOrder);
  const orderLocation =
    locationIds.length === 1
      ? locationsQ.data?.items.find((location) => location.id === locationIds[0]) ??
        {
          id: locationIds[0]!,
          name: activeOrder.lines.find((line) => line.locationId === locationIds[0])?.locationName ?? '',
        }
      : null;
  const activeSupplier =
    suppliersQ.data?.items.find((supplier) => supplier.id === activeOrder.supplierId) ?? null;
  const activeSupplierMetadata = activeSupplier?.metadata;
  const onlinePlatformUrl = supplierMetadataString(activeSupplierMetadata, 'channelTarget').trim();
  const isCoupangPlatform = isCoupangOnlineSupplier(activeSupplier, activeSupplierMetadata, onlinePlatformUrl);
  const canOpenOnlinePlatform =
    isDraft &&
    !!activeSupplierMetadata &&
    isOnlineSupplier(activeSupplierMetadata) &&
    /^https?:\/\//i.test(onlinePlatformUrl);
  const canPrepareCoupangMcp =
    canOpenOnlinePlatform &&
    isCoupangPlatform &&
    activeOrder.lines.length > 0 &&
    activeOrder.lines.every((line) => Number(lineQty[line.id] ?? line.quantity) > 0);
  const coupangMcpBlockedReason = !isCoupangPlatform
    ? '쿠팡 온라인 거래처가 아닙니다.'
    : !canOpenOnlinePlatform
      ? '쿠팡 주문 페이지 주소가 없습니다.'
      : activeOrder.lines.length === 0
        ? '발주 품목이 없습니다.'
        : !canPrepareCoupangMcp
          ? '발주 수량을 확인해야 합니다.'
          : '';
  const coupangRunnerStatus = coupangRunnerJob?.status;
  const coupangMcpStages: CoupangMcpStageView[] = [
    {
      label: '처리할 발주서 생성',
      status: 'done',
      detail: `${activeOrder.lines.length}개 발주 항목`,
    },
    {
      label: '발주서 검토 / 수량 확인',
      status: canPrepareCoupangMcp ? 'done' : 'active',
      detail: '발주서 수량을 그대로 사용',
    },
    {
      label: '쿠팡 MCP 실행',
      status: !isCoupangPlatform
        ? 'blocked'
        : coupangRunnerStatus === 'cart_ready'
          ? 'done'
          : coupangRunnerStatus === 'queued' || coupangRunnerStatus === 'running'
            ? 'active'
            : coupangRunnerStatus
              ? 'blocked'
              : canPrepareCoupangMcp
                ? 'active'
                : 'blocked',
      detail: coupangRunnerStatus
        ? `runner ${onlineOrderRunnerStatusLabel(coupangRunnerStatus)}`
        : '서버 runner job 생성 대기',
    },
    {
      label: '사용자 쿠팡 결제',
      status: coupangRunnerStatus === 'cart_ready'
          ? 'active'
          : coupangRunnerStatus
            ? 'pending'
            : 'blocked',
      detail: '쿠팡 결제 화면에서 사용자가 직접 처리',
    },
    {
      label: '결과 회수 / 상태 처리',
      status: coupangRunnerStatus === 'cart_ready' ? 'pending' : 'blocked',
      detail: '주문 증거 확인 후 별도 승인으로 처리',
    },
  ];

  function openPlatformDraft() {
    if (!canOpenOnlinePlatform) {
      toast.error('온라인 거래처의 주문 페이지 주소가 없습니다.');
      return;
    }
    window.open(onlinePlatformUrl, '_blank', 'noopener,noreferrer');
  }

  async function startCoupangRunnerJob() {
    if (!canPrepareCoupangMcp) {
      toast.error(coupangMcpBlockedReason || '쿠팡 MCP 실행 조건을 확인하세요.');
      return;
    }

    setCoupangRunnerBusy(true);
    setCoupangRunnerError(null);
    try {
      const result = await callDvvbApi<OnlineOrderRunnerJobCreateResponse>(
        `/v1/online-order-runner/orders/${activeOrder.id}/jobs`,
        {
          method: 'POST',
          body: JSON.stringify({ executionMode: 'live_cart' }),
        },
      );
      setCoupangRunnerJob(result.item);
      setCoupangRunnerJobId(result.item.jobId);
      setCoupangMcpPreparedAt(new Date().toISOString());
      toast.success('서버 runner에 쿠팡 장바구니 작업을 생성했습니다.');
    } catch (e) {
      const err = e as ApiCallError;
      setCoupangRunnerError(err.message ?? '쿠팡 MCP 실행 실패');
      toast.error(err.message ?? '쿠팡 MCP 실행 실패');
    } finally {
      setCoupangRunnerBusy(false);
    }
  }

  async function refreshCoupangRunnerJob() {
    if (!coupangRunnerJobId) {
      toast.error('조회할 쿠팡 MCP 작업이 없습니다.');
      return;
    }

    setCoupangRunnerPolling(true);
    try {
      const result = await callDvvbApi<OnlineOrderRunnerJobResponse>(
        `/v1/online-order-runner/jobs/${encodeURIComponent(coupangRunnerJobId)}`,
      );
      setCoupangRunnerJob(result.item);
      setCoupangRunnerError(null);
      toast.success('쿠팡 MCP 상태를 갱신했습니다.');
    } catch (e) {
      const err = e as ApiCallError;
      setCoupangRunnerError(err.message ?? '쿠팡 MCP 상태 조회 실패');
      toast.error(err.message ?? '쿠팡 MCP 상태 조회 실패');
    } finally {
      setCoupangRunnerPolling(false);
    }
  }

  async function openCoupangRunnerBrowser() {
    if (!coupangRunnerJobId) {
      toast.error('조회할 쿠팡 MCP 작업이 없습니다.');
      return;
    }

    setCoupangRunnerPolling(true);
    try {
      const result = await callDvvbApi<OnlineOrderRunnerJobBrowserResponse>(
        `/v1/online-order-runner/jobs/${encodeURIComponent(coupangRunnerJobId)}/browser/cart`,
        { method: 'POST' },
      );
      setCoupangRunnerJob(result.item);
      const browserUrl = result.browser?.url ?? result.item.browserUrl;
      if (!browserUrl) {
        toast.error('MCP 브라우저 URL이 없습니다. 상태 새로고침 후 다시 시도하세요.');
        return;
      }
      window.open(browserUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      const err = e as ApiCallError;
      toast.error(err.message ?? 'MCP 브라우저 열기 실패');
    } finally {
      setCoupangRunnerPolling(false);
    }
  }

  async function openSmsDraft() {
    if (!activeOrder.supplier?.smsRecipient?.trim()) {
      toast.error('거래처 SMS 수신 번호가 없습니다.');
      return;
    }
    if (locationIds.length === 0) {
      toast.error('발주서의 사업장 정보를 확인할 수 없습니다.');
      return;
    }
    if (locationIds.length > 1) {
      toast.error('여러 사업장 재료가 섞인 발주서는 문자 작성 전에 사업장별로 나눠야 합니다.');
      return;
    }
    const sender = readOrderSmsSenderSettings(orderLocation?.settings);
    if (isPhoneNumberText(sender.businessName)) {
      toast.error('사업장 상호명에 전화번호가 들어가 있습니다. 설정 > 매장분류에서 상호명을 수정하세요.');
      return;
    }
    if (samePhoneNumber(activeOrder.supplier.smsRecipient, sender.phone)) {
      toast.error('거래처 SMS 수신 번호가 발신자 연락처와 같습니다. 거래처 전화번호를 확인하세요.');
      return;
    }
    const body = buildOrderSmsBody(activeOrder, lineQty, orderLocation);
    try {
      await navigator.clipboard?.writeText(body);
      toast.success('문자 본문을 복사하고 문자앱을 엽니다.');
    } catch {
      toast.success('문자앱을 엽니다.');
    }
    window.location.href = smsDraftUri(activeOrder.supplier.smsRecipient, body);
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`발주서 — ${order.supplier?.name ?? '(거래처 없음)'}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            닫기
          </Button>
          {canExcludeFromPending && (
            <Button
              variant="ghost"
              onClick={() => {
                if (window.confirm(orderPendingExcludeConfirm(activeOrder))) {
                  cancelMu.mutate();
                }
              }}
              disabled={cancelMu.isPending || sendMu.isPending}
              style={{ color: 'var(--danger)' }}
            >
              {cancelMu.isPending
                ? orderPendingExcludePendingLabel(activeOrder)
                : orderPendingExcludeLabel(activeOrder)}
            </Button>
          )}
          {isDraft && (
            <>
              <Button
                variant="secondary"
                onClick={() => saveMu.mutate()}
                disabled={saveMu.isPending || cancelMu.isPending || sendMu.isPending}
              >
                {saveMu.isPending ? '저장 중…' : '수량 저장'}
              </Button>
              <Button
                variant="primary"
                onClick={openSmsDraft}
                disabled={cancelMu.isPending || sendMu.isPending || saveMu.isPending || locationsQ.isLoading}
              >
                문자앱 열기
              </Button>
              {isCoupangPlatform ? (
                <>
                  <Button
                    variant="primary"
                    onClick={startCoupangRunnerJob}
                    disabled={
                      coupangRunnerBusy
                      || cancelMu.isPending
                      || sendMu.isPending
                      || saveMu.isPending
                      || suppliersQ.isLoading
                      || !canPrepareCoupangMcp
                    }
                  >
                    {coupangRunnerBusy
                      ? 'MCP 실행 중…'
                      : coupangRunnerJob
                        ? '쿠팡 MCP 재실행'
                        : '쿠팡 MCP 실행'}
                  </Button>
                  {canOpenOnlinePlatform && (
                    <Button
                      variant="secondary"
                      onClick={openPlatformDraft}
                      disabled={cancelMu.isPending || sendMu.isPending || saveMu.isPending || suppliersQ.isLoading}
                    >
                      쿠팡 페이지 열기
                    </Button>
                  )}
                </>
              ) : (
                canOpenOnlinePlatform && (
                  <Button
                    variant="secondary"
                    onClick={openPlatformDraft}
                    disabled={cancelMu.isPending || sendMu.isPending || saveMu.isPending || suppliersQ.isLoading}
                  >
                    플랫폼 페이지 열기
                  </Button>
                )
              )}
              <Button
                variant="strong"
                onClick={() => sendMu.mutate()}
                disabled={sendMu.isPending || cancelMu.isPending || saveMu.isPending}
              >
                {sendMu.isPending ? '처리 중…' : '발송 완료 처리'}
              </Button>
            </>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
        {/* Header info */}
        <div
          style={{
            padding: '8px 12px',
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 12,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 6,
            columnGap: 12,
            minWidth: 0,
          }}
        >
          <div style={{ color: 'var(--fg-3)' }}>상태</div>
          <div>
            <Badge variant={status.tone}>{status.label}</Badge>
            {order.isAutoGenerated && (
              <span style={{ marginLeft: 6, color: 'var(--primary-strong)', fontSize: 11 }}>
                · 자동 생성
              </span>
            )}
          </div>
          <div style={{ color: 'var(--fg-3)' }}>생성</div>
          <div style={{ color: 'var(--fg-2)' }}>
            {new Date(order.createdAt).toLocaleString('ko-KR')}
          </div>
          {order.sentAt && (
            <>
              <div style={{ color: 'var(--fg-3)' }}>발송</div>
              <div style={{ color: 'var(--fg-2)' }}>
                {new Date(order.sentAt).toLocaleString('ko-KR')}
              </div>
            </>
          )}
          {order.notes && (
            <>
              <div style={{ color: 'var(--fg-3)' }}>비고</div>
              <div style={{ color: 'var(--fg-2)' }}>{order.notes}</div>
            </>
          )}
        </div>

        {activeOrder.status === 'sent' && (
          <AlertBanner
            tone="warning"
            message="발송됨 발주서는 자동발주 계산에서 발주중으로 잡힙니다. 실제 물품이 못 들어왔거나 재생성이 필요하면 발주중 제외로 계산에서 빼고, 실제 입고는 재료 화면에서 처리하세요."
          />
        )}

        {isDraft && isOnlineSupplier(activeSupplierMetadata) && (
          <div className={`dvvb-mcp-review ${isCoupangPlatform ? 'is-coupang' : 'is-manual'}`}>
            <div className="dvvb-mcp-review-head">
              <div>
                <div className="dvvb-mcp-review-title">
                  {isCoupangPlatform ? '쿠팡 MCP 실행 단계' : '온라인 플랫폼 발주'}
                </div>
                <div className="dvvb-mcp-review-sub">
                  {isCoupangPlatform
                    ? coupangMcpPreparedAt
                      ? '서버 runner 작업 생성됨'
                      : canPrepareCoupangMcp
                        ? '검토 후 서버 runner 실행 가능'
                        : coupangMcpBlockedReason
                    : '등록된 플랫폼 페이지를 수동으로 엽니다.'}
                </div>
              </div>
              <Badge variant={isCoupangPlatform && canPrepareCoupangMcp ? 'success' : 'warning'}>
                {isCoupangPlatform ? '쿠팡' : '수동'}
              </Badge>
            </div>
            {isCoupangPlatform && (
              <>
                <div className="dvvb-mcp-stage-grid">
                  {coupangMcpStages.map((stage, index) => (
                    <div className={`dvvb-mcp-stage is-${stage.status}`} key={stage.label}>
                      <span>{index + 1}</span>
                      <div>
                        <strong>{stage.label}</strong>
                        <em>{stage.detail}</em>
                      </div>
                    </div>
                  ))}
                </div>
                {coupangRunnerError && (
                  <AlertBanner tone="warning" message={coupangRunnerError} />
                )}
                {coupangRunnerJob && (
                  <div className="dvvb-mcp-runner">
                    <div className="dvvb-mcp-runner-head">
                      <div>
                        <strong>서버 runner 작업</strong>
                        <span>
                          {coupangRunnerJob.jobId} · {new Date(coupangRunnerJob.updatedAt).toLocaleString('ko-KR')}
                        </span>
                      </div>
                      <div className="dvvb-mcp-runner-badges">
                        <Badge variant={onlineOrderRunnerBadgeTone(coupangRunnerJob.status)}>
                          {onlineOrderRunnerStatusLabel(coupangRunnerJob.status)}
                        </Badge>
                        <Badge variant="info">
                          {coupangRunnerJob.executionMode === 'live_cart'
                            ? '실장바구니'
                            : coupangRunnerJob.executionMode === 'dry_run'
                              ? 'read-only'
                              : coupangRunnerJob.executionMode}
                        </Badge>
                      </div>
                    </div>
                    <div className="dvvb-mcp-runner-lines">
                      {coupangRunnerJob.lines.map((line) => (
                        <div
                          className={`dvvb-mcp-runner-line ${line.blockers.length === 0 ? 'is-ready' : 'is-blocked'}`}
                          key={line.lineId}
                        >
                          <strong>{line.materialName}</strong>
                          <span>
                            {formatNumber(line.requestedQuantity)}
                            {line.orderUnit ? ` ${line.orderUnit}` : ''}
                          </span>
                          <em>
                            {line.productTitle
                              ? `${line.productTitle}${line.optionText ? ` / ${line.optionText}` : ''}`
                              : `상태: ${line.status}`}
                          </em>
                          {line.blockers.length > 0 ? (
                            <small>{line.blockers.join(', ')}</small>
                          ) : (
                            <small>runner evidence 수집 중</small>
                          )}
                        </div>
                      ))}
                    </div>
                    {coupangRunnerJob.blockers.length > 0 && (
                      <div className="dvvb-mcp-result-note">
                        blocker: {coupangRunnerJob.blockers.join(', ')}
                      </div>
                    )}
                    <div className="dvvb-mcp-runner-cart">
                      <span>
                        서버 runner가 승인된 범위에서 쿠팡 장바구니를 준비하고 결제 단계 진입 증거를 수집합니다. 최종 결제와 외부 주문 취소는 사용자가 직접 확인합니다.
                      </span>
                      {coupangRunnerJob.evidence?.jobDir && (
                        <small>{coupangRunnerJob.evidence.jobDir}</small>
                      )}
                    </div>
                    <div className="dvvb-mcp-runner-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={refreshCoupangRunnerJob}
                        disabled={coupangRunnerPolling}
                      >
                        {coupangRunnerPolling ? '상태 확인 중…' : '상태 새로고침'}
                      </Button>
                      {coupangRunnerJob.status === 'cart_ready' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={openCoupangRunnerBrowser}
                          disabled={coupangRunnerPolling}
                        >
                          MCP 브라우저에서 장바구니 보기
                        </Button>
                      )}
                      {coupangRunnerJob.status === 'cart_ready' && (
                        <Button
                          variant="strong"
                          size="sm"
                          onClick={() => writebackMu.mutate()}
                          disabled={writebackMu.isPending}
                        >
                          {writebackMu.isPending ? '반영 중…' : 'DVVB 발송 완료 반영'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Lines table */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg-3)',
              textTransform: 'uppercase',
              letterSpacing: 0,
              marginBottom: 6,
            }}
          >
            발주 품목 ({order.lines.length})
          </div>
          <div
            style={{
              border: '1px solid var(--border-weak)',
              borderRadius: 'var(--radius-sm)',
              overflowY: 'hidden',
              ...modalTableScrollStyle,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: REVIEW_LINE_COLUMNS,
                gap: 10,
                padding: '8px 12px',
                background: 'var(--bg-subtle)',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--fg-3)',
                textTransform: 'uppercase',
                letterSpacing: 0,
                minWidth: REVIEW_LINE_MIN_WIDTH,
                boxSizing: 'border-box',
              }}
            >
              <div>품목</div>
              <div style={{ textAlign: 'right' }}>수량</div>
              <div style={{ textAlign: 'right' }}>단가</div>
              <div style={{ textAlign: 'right' }}>합계</div>
            </div>
            {order.lines.map((l, i) => {
              const qStr = lineQty[l.id] ?? l.quantity;
              const q = Number(qStr);
              const u = Number(l.unitCost);
              const total = Number.isFinite(q) && Number.isFinite(u) ? Math.round(q * u) : 0;
              return (
                <div
                  key={l.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: REVIEW_LINE_COLUMNS,
                    gap: 10,
                    padding: '8px 12px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border-weak)',
                    fontSize: 13,
                    alignItems: 'center',
                    minWidth: REVIEW_LINE_MIN_WIDTH,
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 500,
                      color: 'var(--fg-1)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {l.inventoryName ?? '(재료 없음)'}
                    {l.unit && (
                      <span style={{ color: 'var(--fg-3)', marginLeft: 4, fontSize: 11 }}>
                        ({l.unit})
                      </span>
                    )}
                  </div>
                  <div>
                    {isDraft ? (
                      <TextInput
                        type="number"
                        value={qStr}
                        onChange={(v) =>
                          setLineQty((prev) => ({ ...prev, [l.id]: v }))
                        }
                      />
                    ) : (
                      <div
                        style={{
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color: 'var(--fg-1)',
                        }}
                      >
                        {formatNumber(qStr)}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      fontSize: 12,
                      color: 'var(--fg-3)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatWon(l.unitCost)}
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'var(--fg-1)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatWon(total)}
                  </div>
                </div>
              );
            })}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: REVIEW_LINE_COLUMNS,
                gap: 10,
                padding: '10px 12px',
                background: 'var(--bg-subtle)',
                borderTop: '1px solid var(--border-weak)',
                fontSize: 13,
                alignItems: 'center',
                minWidth: REVIEW_LINE_MIN_WIDTH,
                boxSizing: 'border-box',
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--fg-1)' }}>합계</div>
              <div />
              <div />
              <div
                style={{
                  textAlign: 'right',
                  fontWeight: 700,
                  color: 'var(--fg-1)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatWon(totalAmount)}
              </div>
            </div>
          </div>
          {isDraft && (
            <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 6 }}>
              * 수량 편집은 수량 저장 또는 발송 완료 처리 시 실제 발주서에 반영됩니다. 합계는 화면 표시와 저장 전 검토용입니다.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── OrderCreateModal — 수동 draft 생성 (supplier/location 선택 + 재료 검색) ───
interface DraftLineState {
  inventoryId: string;
  inventoryName: string;
  locationName: string;
  unit: string;
  quantity: string;
  unitCost: string;
}

function draftLineFromMaterial(material: MaterialRow, quantity = 1): DraftLineState {
  return {
    inventoryId: material.id,
    inventoryName: material.name,
    locationName: material.locationName || '사업장 미확인',
    unit: material.comparableUnit || material.unit,
    quantity: String(quantity),
    unitCost: String(material.cost ?? 0),
  };
}

function OrderCreateModal({
  open,
  initialSupplierId,
  initialLocationId,
  initialInventoryIds = [],
  initialInventoryQuantities = {},
  candidateBlockReason = null,
  onClose,
}: {
  open: boolean;
  initialSupplierId?: string;
  initialLocationId?: string;
  initialInventoryIds?: string[];
  initialInventoryQuantities?: Record<string, number>;
  /** 후보 기반(시드) 발주서 — 발주중 수량 조회가 완료되지 않은 이유가 있으면 생성 차단. 수동 작성은 해당 없음. */
  candidateBlockReason?: PendingOrdersGateReason | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [supplierId, setSupplierId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [lines, setLines] = useState<DraftLineState[]>([]);
  const [materialSearch, setMaterialSearch] = useState('');
  const [errors, setErrors] = useState<{ supplierId?: string; locationId?: string; lines?: string }>({});

  const suppliersQ = useQuery({
    queryKey: ['suppliers', 'list'],
    queryFn: async () => {
      const r = await suppliersControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<SuppliersEnvelope>(r);
    },
    enabled: open,
  });

  const materialsQ = useQuery({
    queryKey: ['inventory', 'materials', 'auto-order-candidates'],
    queryFn: listAllAutoOrderMaterials,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setSupplierId(initialSupplierId ?? '');
    setLocationId(initialLocationId ?? '');
    setNotes('');
    setExpectedDate('');
    setLines([]);
    setMaterialSearch('');
    setErrors({});
  }, [initialLocationId, initialSupplierId, open]);

  const suppliers = suppliersQ.data?.items ?? [];
  const materials = materialsQ.data?.items ?? [];
  const selectedSupplier = suppliers.find((supplier) => supplier.id === supplierId) ?? null;
  const initialInventoryIdSet = useMemo(
    () => new Set(initialInventoryIds),
    [initialInventoryIds],
  );
  const initialInventoryQuantityMap = useMemo(
    () => new Map(Object.entries(initialInventoryQuantities)),
    [initialInventoryQuantities],
  );
  const isManualMode = initialInventoryIdSet.size === 0;
  const candidateBlocked = !isManualMode && candidateBlockReason != null;
  const hasSeededSupplier = !isManualMode && Boolean(initialSupplierId);
  const hasSeededLocation = !isManualMode && Boolean(initialLocationId);
  const selectedSupplierItems = useMemo(
    () => supplierSupplyItems(selectedSupplier).filter((item) => item.autoEnabled !== '미사용'),
    [selectedSupplier],
  );
  const supplierItemBySourceId = useMemo(() => {
    const byId = new Map<string, SupplierSupplyItem>();
    selectedSupplierItems.forEach((item) => {
      if (item.sourceId) byId.set(item.sourceId, item);
    });
    return byId;
  }, [selectedSupplierItems]);
  const mappedShortageMaterials = useMemo(() => {
    return materials.filter((material) => {
      if (!supplierItemBySourceId.has(material.id)) return false;
      return materialShortage(material) > 0;
    });
  }, [materials, supplierItemBySourceId]);
  const allMaterialLocations = useMemo(
    () => materialLocationOptions(materials),
    [materials],
  );
  const candidateLocations = useMemo(
    () => materialLocationOptions(mappedShortageMaterials),
    [mappedShortageMaterials],
  );
  const locationOptions = isManualMode ? allMaterialLocations : candidateLocations;
  const visibleCandidateMaterials = useMemo(() => {
    if (candidateLocations.length > 1 && !locationId) return [];
    const scopedMaterials = locationId
      ? mappedShortageMaterials.filter((material) => material.locationId === locationId)
      : mappedShortageMaterials;
    if (initialInventoryIdSet.size === 0) return scopedMaterials;
    return scopedMaterials.filter((material) => initialInventoryIdSet.has(material.id));
  }, [candidateLocations.length, initialInventoryIdSet, locationId, mappedShortageMaterials]);
  const materialSearchResults = useMemo(() => {
    if (!supplierId || !locationId) return [];
    const query = materialSearch.trim().toLocaleLowerCase('ko-KR');
    return materials
      .filter((material) => material.locationId === locationId)
      .filter((material) => material.status !== 'deleted')
      .filter((material) => {
        if (!query) return true;
        const haystack = `${material.name} ${material.unit} ${material.comparableUnit ?? ''}`.toLocaleLowerCase('ko-KR');
        return haystack.includes(query);
      })
      .filter((material) => !lines.some((line) => line.inventoryId === material.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
      .slice(0, 8);
  }, [lines, locationId, materialSearch, materials, supplierId]);

  useEffect(() => {
    if (!supplierId) {
      setLocationId('');
      return;
    }
    if (!selectedSupplier || !materialsQ.data) return;
    if (hasSeededSupplier) {
      if (supplierId !== initialSupplierId) setSupplierId(initialSupplierId ?? '');
      return;
    }
    if (hasSeededLocation) {
      if (locationId !== initialLocationId) setLocationId(initialLocationId ?? '');
      return;
    }
    if (locationOptions.length === 1) {
      const only = locationOptions[0]!;
      if (locationId !== only.id) setLocationId(only.id);
      return;
    }
    if (locationId && !locationOptions.some((location) => location.id === locationId)) {
      setLocationId('');
    }
  }, [hasSeededLocation, hasSeededSupplier, initialLocationId, initialSupplierId, locationId, locationOptions, materialsQ.data, selectedSupplier, supplierId]);

  useEffect(() => {
    if (!supplierId) {
      setLines([]);
      return;
    }
    if (isManualMode) return;
    setLines(
      visibleCandidateMaterials.map((it) => {
        const seededQuantity = initialInventoryQuantityMap.get(it.id);
        const shortage = roundOrderQuantity(
          typeof seededQuantity === 'number' ? seededQuantity : materialShortage(it),
        );
        const supplyItem = supplierItemBySourceId.get(it.id);
        return {
          inventoryId: it.id,
          inventoryName: supplyItem?.supplierName || it.name,
          locationName: it.locationName || supplyItem?.sourceLocationName || '사업장 미확인',
          unit: it.comparableUnit ?? it.unit,
          quantity: String(shortage),
          unitCost: String(it.cost ?? 0),
        };
      }),
    );
  }, [initialInventoryQuantityMap, isManualMode, supplierId, supplierItemBySourceId, visibleCandidateMaterials]);

  const totalAmount = useMemo(
    () =>
      lines.reduce((acc, l) => {
        const q = Number(l.quantity);
        const u = Number(l.unitCost);
        return acc + (Number.isFinite(q) && Number.isFinite(u) ? Math.round(q * u) : 0);
      }, 0),
    [lines],
  );

  const createMu = useMutation({
    mutationFn: async () => {
      if (!isManualMode) {
        // 후보 기반 발주서 = 실행 직전에 발주중 수량 조회 상태를 다시 확인 (열린 생성창·비활성 버튼 우회 차단).
        const gate = pendingOrdersGate(queryClient.getQueryState(PENDING_ORDERS_QUERY_KEY));
        if (gate.reason) throw new Error(pendingOrdersBlockMessage(gate.reason));
      }
      const validLines = lines
        .filter((l) => l.inventoryId && Number(l.quantity) > 0)
        .map((l) => ({
          inventoryId: l.inventoryId,
          quantity: Number(l.quantity),
          unitCost: Number(l.unitCost) || 0,
        }));
      const body: Record<string, unknown> = {
        supplierId,
        lines: validLines,
      };
      if (notes.trim()) body.notes = notes.trim();
      if (expectedDate.trim()) body.expectedDate = expectedDate.trim();
      const r = await ordersControllerCreate({ body: body as never });
      return unwrap<{ item: OrderRow }>(r);
    },
    onSuccess: async () => {
      toast.success('발주서 draft 를 생성했습니다.');
      await refreshAutoOrderQueries();
      onClose();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '발주서 생성 실패');
    },
  });

  function submit() {
    const next: typeof errors = {};
    if (!supplierId) next.supplierId = '거래처를 선택하세요.';
    if (supplierId && isManualMode && !locationId) {
      next.locationId = '사업장을 선택하세요.';
    } else if (supplierId && !isManualMode && candidateLocations.length > 1 && !locationId) {
      next.locationId = '사업장을 선택하세요.';
    }
    const validLines = lines.filter(
      (l) => l.inventoryId && Number(l.quantity) > 0,
    );
    if (validLines.length === 0) {
      next.lines = '품목을 1개 이상 추가하고 수량 > 0 으로 입력하세요.';
    }
    if (candidateBlocked && candidateBlockReason) {
      next.lines = pendingOrdersBlockMessage(candidateBlockReason);
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    createMu.mutate();
  }

  function addDraftMaterial(material: MaterialRow) {
    setLines((current) => {
      if (current.some((line) => line.inventoryId === material.id)) return current;
      return [...current, draftLineFromMaterial(material)];
    });
    setMaterialSearch('');
    setErrors((current) => ({ ...current, lines: undefined }));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initialInventoryIdSet.size > 0 ? '발주서 생성' : '수동 발주서 작성'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMu.isPending}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={submit}
            disabled={createMu.isPending || suppliersQ.isLoading || candidateBlocked}
            title={candidateBlocked && candidateBlockReason ? pendingOrdersBlockMessage(candidateBlockReason) : undefined}
          >
            {createMu.isPending ? '저장 중…' : '발주서 생성'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
        {candidateBlocked && candidateBlockReason && candidateBlockReason !== 'refetching' && (
          <AlertBanner
            tone={candidateBlockReason === 'error' ? 'danger' : 'info'}
            message={pendingOrdersBlockMessage(candidateBlockReason)}
          />
        )}
        <FormField label="거래처" required error={errors.supplierId}>
          <Select
            value={supplierId}
            onChange={(value) => {
              if (hasSeededSupplier) return;
              setSupplierId(value);
              setLocationId('');
              setLines([]);
              setMaterialSearch('');
            }}
            disabled={hasSeededSupplier}
            options={[
              {
                value: '',
                label: hasSeededSupplier
                  ? suppliers.find((s) => s.id === initialSupplierId)?.name ?? '거래처 고정'
                  : '거래처 선택…',
              },
              ...suppliers.map((s) => ({
                value: s.id,
                label: s.autoOrderEnabled ? `${s.name} (자동)` : s.name,
              })),
            ]}
          />
        </FormField>

        <FormField
          label="사업장"
          required={isManualMode || (!hasSeededLocation && candidateLocations.length > 1)}
          error={errors.locationId}
          hint={
            isManualMode
              ? '선택한 사업장의 재료만 검색해 한 발주서에 담습니다.'
              : hasSeededSupplier || hasSeededLocation
                ? '이 발주서는 카드에서 선택한 거래처와 사업장으로 고정됩니다.'
              : '공급물품에 연결된 재료의 사업장 기준으로 발주서를 분리합니다.'
          }
        >
          <Select
            value={locationId}
            onChange={(value) => {
              if (hasSeededLocation) return;
              setLocationId(value);
              if (isManualMode) {
                setLines([]);
                setMaterialSearch('');
              }
            }}
            disabled={!supplierId || hasSeededLocation || locationOptions.length <= 1}
            options={[
              {
                value: '',
                label:
                  !supplierId
                    ? '거래처를 먼저 선택하세요'
                    : hasSeededLocation
                      ? locationOptions[0]?.name ?? '사업장 고정'
                      : locationOptions.length === 0
                      ? isManualMode
                        ? '등록된 재료 사업장 없음'
                        : '자동발주 후보 사업장 없음'
                      : locationOptions.length === 1
                        ? locationOptions[0]!.name
                        : '사업장 선택…',
              },
              ...locationOptions.map((location) => ({
                value: location.id,
                label: location.name,
              })),
            ]}
          />
        </FormField>

        {supplierId && materialsQ.data && (
          <div
            style={{
              padding: '8px 12px',
              background: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              color: 'var(--fg-3)',
            }}
          >
            {isManualMode ? (
              <>
                수동 발주서는 처리 필요 재료 여부와 관계없이 선택한 사업장의 재료를 검색해 추가합니다.
                {locationId
                  ? ` 현재 발주서에는 ${lines.length}개 품목이 담겼습니다.`
                  : ' 사업장을 선택한 뒤 재료를 검색하세요.'}
              </>
            ) : (
              <>
                공급물품 매핑 {selectedSupplierItems.length}개 중 자동발주 후보 {mappedShortageMaterials.length}개를 확인했습니다.
                {candidateLocations.length > 1 && !locationId
                  ? ' 사업장을 선택하면 해당 사업장 후보만 채워집니다.'
                  : ` 현재 발주서에는 ${lines.length}개 품목이 채워졌습니다.`}
                {initialInventoryIdSet.size > 0 ? ' 후보 카드에서 확인 완료한 품목을 기본으로 담고, 같은 사업장 재료를 추가 검색할 수 있습니다.' : ''}
              </>
            )}
          </div>
        )}

        {supplierId && (
          <FormField
            label={isManualMode ? '재료 검색' : '재료 추가 검색'}
            hint={
              isManualMode
                ? '처리 필요 재료가 아니어도 선택한 사업장의 등록 재료를 발주 품목으로 추가할 수 있습니다.'
                : '후보에 없는 재료도 같은 사업장의 등록 재료라면 발주 품목으로 추가할 수 있습니다.'
            }
          >
            <div style={{ display: 'grid', gap: 8 }}>
              <TextInput
                value={materialSearch}
                onChange={setMaterialSearch}
                disabled={!locationId || materialsQ.isLoading}
                placeholder={
                  !locationId
                    ? '사업장을 먼저 선택하세요'
                    : '재료명, 단위로 검색'
                }
              />
              {locationId && (
                <div
                  style={{
                    border: '1px solid var(--border-weak)',
                    borderRadius: 'var(--radius-sm)',
                    overflow: 'hidden',
                  }}
                >
                  {materialsQ.isLoading ? (
                    <div style={{ padding: 10, fontSize: 12, color: 'var(--fg-3)' }}>
                      재료를 불러오는 중...
                    </div>
                  ) : materialSearchResults.length === 0 ? (
                    <div style={{ padding: 10, fontSize: 12, color: 'var(--fg-3)' }}>
                      추가할 수 있는 재료가 없습니다.
                    </div>
                  ) : (
                    materialSearchResults.map((material) => (
                      <div
                        key={material.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(160px, 1fr) 96px',
                          gap: 8,
                          alignItems: 'center',
                          padding: '8px 10px',
                          borderTop: '1px solid var(--border-weak)',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: 'var(--fg-1)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {material.name}
                            <span style={{ color: 'var(--fg-3)', fontSize: 11, marginLeft: 4 }}>
                              ({material.comparableUnit || material.unit})
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                            현재 {formatNumber(material.comparableQuantity ?? material.stock)} {material.comparableUnit || material.unit}
                            {' · '}
                            단가 {formatWon(material.cost ?? 0)}
                          </div>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => addDraftMaterial(material)}
                        >
                          추가
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </FormField>
        )}

        {/* Lines */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--fg-3)',
                textTransform: 'uppercase',
                letterSpacing: 0,
              }}
            >
              발주 품목 ({lines.length})
            </div>
            <div style={{ flex: 1 }} />
          </div>
          {errors.lines && (
            <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 6 }}>
              {errors.lines}
            </div>
          )}
          {lines.length === 0 ? (
            <div
              style={{
                padding: '14px 12px',
                background: 'var(--bg-subtle)',
                border: '1px dashed var(--border-base)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                color: 'var(--fg-3)',
                textAlign: 'center',
              }}
            >
              {supplierId
                ? isManualMode
                  ? locationId
                    ? '재료를 검색해서 발주 품목을 추가하세요.'
                    : '사업장을 선택하세요. 여러 사업장 재료는 한 발주서에 섞지 않습니다.'
                  : candidateLocations.length > 1 && !locationId
                    ? '사업장을 선택하세요. 여러 사업장 재료는 한 발주서에 섞지 않습니다.'
                    : '이 거래처에 매핑된 자동발주 후보 재료가 없습니다. 거래처 공급물품 매핑과 재료의 처리 필요 상태를 확인하세요.'
                : '거래처를 먼저 선택하세요.'}
            </div>
          ) : (
            <div
              style={{
                border: '1px solid var(--border-weak)',
                borderRadius: 'var(--radius-sm)',
                overflowY: 'hidden',
                ...modalTableScrollStyle,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: CREATE_LINE_COLUMNS,
                  gap: 8,
                  padding: '8px 12px',
                  background: 'var(--bg-subtle)',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--fg-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0,
                  minWidth: CREATE_LINE_MIN_WIDTH,
                  boxSizing: 'border-box',
                }}
              >
                <div>품목</div>
                <div style={{ textAlign: 'right' }}>수량</div>
                <div style={{ textAlign: 'right' }}>단가</div>
                <div style={{ textAlign: 'right' }}>합계</div>
                <div />
              </div>
              {lines.map((l, idx) => {
                const q = Number(l.quantity);
                const u = Number(l.unitCost);
                const total = Number.isFinite(q) && Number.isFinite(u) ? Math.round(q * u) : 0;
                return (
                  <div
                    key={`${l.inventoryId}-${idx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: CREATE_LINE_COLUMNS,
                      gap: 8,
                      padding: '8px 12px',
                      borderTop: '1px solid var(--border-weak)',
                      alignItems: 'center',
                      fontSize: 13,
                      minWidth: CREATE_LINE_MIN_WIDTH,
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 500,
                        color: 'var(--fg-1)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {l.inventoryName}
                      {l.unit && (
                        <span style={{ color: 'var(--fg-3)', marginLeft: 4, fontSize: 11 }}>
                          ({l.unit})
                        </span>
                      )}
                      <span style={{ display: 'block', color: 'var(--fg-3)', marginTop: 2, fontSize: 11 }}>
                        {l.locationName}
                      </span>
                    </div>
                    <TextInput
                      type="number"
                      value={l.quantity}
                      onChange={(v) =>
                        setLines((prev) =>
                          prev.map((p, i) => (i === idx ? { ...p, quantity: v } : p)),
                        )
                      }
                    />
                    <TextInput
                      type="number"
                      value={l.unitCost}
                      onChange={(v) =>
                        setLines((prev) =>
                          prev.map((p, i) => (i === idx ? { ...p, unitCost: v } : p)),
                        )
                      }
                    />
                    <div
                      style={{
                        textAlign: 'right',
                        fontWeight: 600,
                        color: 'var(--fg-1)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatWon(total)}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setLines((prev) => prev.filter((_, i) => i !== idx))
                      }
                      style={{ color: 'var(--danger)', padding: '4px 6px' }}
                    >
                      ×
                    </Button>
                  </div>
                );
              })}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: CREATE_LINE_COLUMNS,
                  gap: 8,
                  padding: '10px 12px',
                  background: 'var(--bg-subtle)',
                  borderTop: '1px solid var(--border-weak)',
                  fontSize: 13,
                  alignItems: 'center',
                  minWidth: CREATE_LINE_MIN_WIDTH,
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--fg-1)' }}>합계</div>
                <div />
                <div />
                <div
                  style={{
                    textAlign: 'right',
                    fontWeight: 700,
                    color: 'var(--fg-1)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatWon(totalAmount)}
                </div>
                <div />
              </div>
            </div>
          )}
        </div>

        <FormField label="예상 입고일 (선택)" hint="YYYY-MM-DD 형식">
          <TextInput
            value={expectedDate}
            onChange={setExpectedDate}
            placeholder="2026-04-30"
          />
        </FormField>

        <FormField label="비고 (선택)">
          <Textarea
            value={notes}
            onChange={setNotes}
            placeholder="발주 메모 / 특이사항"
            rows={2}
          />
        </FormField>
      </div>
    </Modal>
  );
}
